import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';
import TeamSelector from '../components/TeamSelector';
import LocationSearch from '../components/LocationSearch';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ShareIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import StripePaymentForm from '../components/StripePaymentForm';
import { LessonSignupPanel, FundraiserPanel } from '../components/EventSignupPanel';

// Event types that can be created as a weekly repeating series
const RECURRING_EVENT_TYPES = ['practice', 'lesson'];

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' }
];

const Events = () => {
  const { user, isCoach } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [confirmState, setConfirmState] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showAllTeams, setShowAllTeams] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEventDetails, setShowEventDetails] = useState(null);
  const [gameScoreDraft, setGameScoreDraft] = useState({ home: '', away: '' });
  const [showSubscriptionDropdown, setShowSubscriptionDropdown] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'calendar' or 'list' (default to list)
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  
  // Check if we should filter to un-RSVP'd events
  const filterUnRsvp = searchParams.get('filter') === 'unrsvp';
  const [remindTimingByEvent, setRemindTimingByEvent] = useState({}); // { [eventId]: 'now' | 'day_of' | 'day_before' }
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [tryoutRegistrants, setTryoutRegistrants] = useState({}); // { [eventId]: registrants[] }
  const [eventDetailsTab, setEventDetailsTab] = useState('details'); // 'details', 'attendees', 'registrants'
  const [showWalkUpRegistration, setShowWalkUpRegistration] = useState(null); // eventId for which to show walk-up registration
  const [walkUpFormData, setWalkUpFormData] = useState({
    playerName: '',
    playerDob: '',
    parentName: '',
    parentEmail: '',
    parentPhone: ''
  });
  const [walkUpSubmitting, setWalkUpSubmitting] = useState(false);
  const [walkUpEventToken, setWalkUpEventToken] = useState(null);
  const [walkUpEventData, setWalkUpEventData] = useState(null);
  const [walkUpStripeConfig, setWalkUpStripeConfig] = useState(null);
  const [walkUpPendingRegistration, setWalkUpPendingRegistration] = useState(null);

  const getEventLocationText = (event) => {
    const location = event?.location || '';
    const address = event?.address || null;
    let addressText = '';
    if (typeof address === 'string') {
      addressText = address;
    } else if (address && typeof address === 'object') {
      addressText = address.address || address.name || '';
    }
    const parts = [location, addressText].filter(Boolean);
    if (parts.length > 0) return parts.join(' - ');
    // Auto-generated practices/games have no free-text location — fall back to the
    // booked facility resource (e.g. "Field 1 — Riverside Sports Complex") so the
    // assignment, and which park it's in, is actually visible.
    if (!event?.facility_resource) return '';
    const parkName = event.facility_resource.facility?.name;
    return parkName ? `${event.facility_resource.name} — ${parkName}` : event.facility_resource.name;
  };

  const getEventMapUrl = (event) => {
    const address = event?.address || null;
    let destination = '';
    if (address && typeof address === 'object' && address.coordinates?.lat && address.coordinates?.lng) {
      destination = `${address.coordinates.lat},${address.coordinates.lng}`;
    } else {
      destination = getEventLocationText(event) || event?.location || '';
    }
    if (!destination) return '';
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
  };

  const copyShareLink = async (eventId) => {
    try {
      const res = await axios.get(`/api/events/${eventId}/share-link`, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { teamId: selectedTeam?.id || undefined }
      });
      await navigator.clipboard.writeText(res.data.url);
      toast.success('Share link copied');
    } catch (err) {
      console.error('Failed to get share link', err);
      toast.error('Could not create share link');
    }
  };

  // Check if user can access walk-up registration (coach, org_admin, app_manager, but not parent)
  const canAccessWalkUpRegistration = () => {
    if (!user) return false;
    return ['coach', 'org_admin', 'app_manager', 'admin'].includes(user.role);
  };

  const canShareEventLink = () => {
    if (!user) return false;
    return ['coach', 'assistant_coach', 'org_admin', 'app_manager', 'admin'].includes(user.role);
  };
  const canConfigureTryoutSettings = !!user && ['org_admin', 'app_manager', 'admin'].includes(user.role);

  const resetWalkUpModalState = () => {
    setShowWalkUpRegistration(null);
    setWalkUpEventToken(null);
    setWalkUpEventData(null);
    setWalkUpStripeConfig(null);
    setWalkUpPendingRegistration(null);
    setWalkUpFormData({
      playerName: '',
      playerDob: '',
      parentName: '',
      parentEmail: '',
      parentPhone: ''
    });
  };

  // Open walk-up registration. Walk-up should behave exactly like a guest sign-up
  // via the public share link, so we open the public share-link page in a new tab
  // and pass a `walkUp=1` flag so that page renders the guest form (ignoring the
  // staff member's logged-in session).
  const openWalkUpRegistration = async (eventId) => {
    try {
      const res = await axios.get(`/api/events/${eventId}/share-link`, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { teamId: selectedTeam?.id || undefined }
      });

      const shareUrl = new URL(res.data.url, window.location.origin);
      const token = shareUrl.searchParams.get('token');
      if (!token) throw new Error('No token in share link');

      const eventRes = await axios.get(`/api/public/events/${encodeURIComponent(token)}`);

      setWalkUpEventToken(token);
      setWalkUpEventData(eventRes.data);
      setShowWalkUpRegistration(eventId);
    } catch (err) {
      console.error('Failed to open walk-up registration', err);
      toast.error('Could not open walk-up registration');
    }
  };

  // Initialize Stripe payment for walk-up registration
  const initializeWalkUpStripePayment = async (payload) => {
    if (!walkUpEventToken) return;
    setWalkUpSubmitting(true);
    try {
      const headers = user?.token
        ? { Authorization: `Bearer ${user.token}` }
        : undefined;
      
      // Create payment intent
      const response = await axios.post(
        `/api/public/events/${encodeURIComponent(walkUpEventToken)}/stripe-payment-intent`,
        {
          guest: payload.guest || null
        },
        { headers }
      );

      if (!response.data?.paymentIntentId) {
        throw new Error('Payment intent creation failed: No payment intent ID returned');
      }

      // Set up Stripe payment form. Registration happens only after successful payment.
      setWalkUpStripeConfig({
        clientSecret: response.data.clientSecret,
        publishableKey: response.data.publishableKey,
        amount: response.data.amount,
        currency: response.data.currency || 'usd'
      });
      setWalkUpPendingRegistration({
        payload,
        paymentIntentId: response.data.paymentIntentId
      });
      toast.success('Complete payment below to finish walk-up registration.');
    } catch (error) {
      console.error('[WalkUp Registration] Error:', error);
      const message = error.response?.data?.message || 'Unable to start Stripe payment.';
      toast.error(message);
      setWalkUpStripeConfig(null);
      setWalkUpPendingRegistration(null);
    } finally {
      setWalkUpSubmitting(false);
    }
  };

  // Handle payment completion for walk-up registration
  // Registration happens only after successful Stripe payment.
  const handleWalkUpPaymentComplete = async (result) => {
    if (!walkUpPendingRegistration || !walkUpEventToken) return;

    if (!result?.success || !result?.paymentIntent) {
      toast.error(result?.error || 'Payment did not complete. Please try again.');
      return;
    }

    const paymentIntentStatus = result.paymentIntent.status;
    if (paymentIntentStatus !== 'succeeded' && paymentIntentStatus !== 'processing') {
      toast.error(`Payment status: ${paymentIntentStatus}. Registration was not created.`);
      return;
    }

    setWalkUpSubmitting(true);
    try {
      const headers = user?.token
        ? { Authorization: `Bearer ${user.token}` }
        : undefined;

      const registerResponse = await axios.post(
        `/api/public/events/${encodeURIComponent(walkUpEventToken)}/register`,
        {
          ...walkUpPendingRegistration.payload,
          stripePaymentIntentId: result.paymentIntent.id,
          walkUp: true
        },
        { headers }
      );

      console.log('[WalkUp Registration] Registration response after payment:', registerResponse.data);
      toast.success('Walk-up registration and payment complete!');

      if (showWalkUpRegistration) {
        setTryoutRegistrants(prev => ({ ...prev, [showWalkUpRegistration]: undefined }));
        fetchTryoutRegistrants(showWalkUpRegistration, { force: true });
      }

      resetWalkUpModalState();
    } catch (err) {
      console.error('[WalkUp Registration] Registration after payment failed:', err);
      toast.error(err.response?.data?.message || 'Payment succeeded but registration failed. Please contact support.');
    } finally {
      setWalkUpSubmitting(false);
    }
  };

  const handleWalkUpRegistration = async (e) => {
    e.preventDefault();
    
    if (!walkUpEventToken) {
      toast.error('Registration token missing');
      return;
    }

    // Validate required fields
    if (!walkUpFormData.playerName.trim()) {
      toast.error('Player name is required');
      return;
    }
    if (!walkUpFormData.playerDob.trim()) {
      toast.error('Player date of birth is required');
      return;
    }
    if (!walkUpFormData.parentName.trim()) {
      toast.error('Parent name is required');
      return;
    }
    if (!walkUpFormData.parentEmail.trim()) {
      toast.error('Parent email is required');
      return;
    }

    // Build registration payload
    const payload = {
      guest: {
        parentName: walkUpFormData.parentName.trim(),
        parentEmail: walkUpFormData.parentEmail.trim(),
        parentPhone: walkUpFormData.parentPhone.trim() || undefined,
        playerName: walkUpFormData.playerName.trim(),
        playerDob: walkUpFormData.playerDob.trim()
      },
      walkUp: true
    };

    // Check if event requires payment
    const registrationFee = walkUpEventData?.registrationFee || 0;
    
    if (registrationFee > 0) {
      // Initialize Stripe payment for paid events
      await initializeWalkUpStripePayment(payload);
    } else {
      // For free events, register directly
      setWalkUpSubmitting(true);
      try {
        const headers = user?.token
          ? { Authorization: `Bearer ${user.token}` }
          : undefined;

        const res = await axios.post(
          `/api/public/events/${encodeURIComponent(walkUpEventToken)}/register`,
          payload,
          { headers }
        );

        if (res.data?.isFree) {
          toast.success('Walk-up registration successful!');
          // Refresh registrants
          if (showWalkUpRegistration) {
            setTryoutRegistrants(prev => ({ ...prev, [showWalkUpRegistration]: undefined }));
            fetchTryoutRegistrants(showWalkUpRegistration, { force: true });
          }
          // Reset and close
          resetWalkUpModalState();
        } else {
          toast.error('Unexpected response from server');
        }
      } catch (err) {
        console.error('Walk-up registration error', err);
        toast.error(err.response?.data?.message || 'Registration failed');
      } finally {
        setWalkUpSubmitting(false);
      }
    }
  };

  const handleRegisterForEvent = async (eventId) => {
    try {
      const token = user?.token || localStorage.getItem('token');
      const res = await axios.get(`/api/events/${eventId}/share-link`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: { teamId: selectedTeam?.id || undefined }
      });
      // Open the registration page in the same window
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Failed to get registration link', err);
      toast.error('Could not get registration link');
    }
  };

  // Check if event is a tryout
  const isTryoutEvent = (event) => {
    // Lessons and fundraisers have their own sign-up flows
    if (event.type === 'lesson' || event.type === 'fundraiser') return false;

    // Check if it's explicitly a tryout type
    if (event.type === 'tryout') {
      console.log(`[isTryoutEvent] Event ${event.id} "${event.title}" is tryout (type === 'tryout')`);
      return true;
    }
    
    // Check notes for tryout flag
    try {
      if (event.notes && typeof event.notes === 'string') {
        const notes = JSON.parse(event.notes);
        if (notes?.tryout === true) {
          console.log(`[isTryoutEvent] Event ${event.id} "${event.title}" is tryout (notes.tryout === true)`);
          return true;
        }
      }
      // Also check if notes is already an object
      if (event.notes && typeof event.notes === 'object') {
        if (event.notes?.tryout === true) {
          console.log(`[isTryoutEvent] Event ${event.id} "${event.title}" is tryout (notes.tryout === true)`);
          return true;
        }
      }
    } catch {
      // If parsing fails, continue to other checks
    }
    
    // Check if isOpenRegistration is true (indicates a tryout with public registration)
    if (event.isOpenRegistration === true) {
      console.log(`[isTryoutEvent] Event ${event.id} "${event.title}" is tryout (isOpenRegistration === true)`);
      return true;
    }
    
    console.log(`[isTryoutEvent] Event ${event.id} "${event.title}" is NOT a tryout. Type: ${event.type}, isOpenRegistration: ${event.isOpenRegistration}`);
    return false;
  };

  // Fetch tryout registrants
  const fetchTryoutRegistrants = async (eventId, { force = false } = {}) => {
    if (!force && tryoutRegistrants[eventId]) {
      console.log(`[Events] Registrants for event ${eventId} already cached:`, tryoutRegistrants[eventId].length);
      return; // Already fetched
    }
    
    console.log(`[Events] Fetching registrants for event ${eventId}...`);
    try {
      // Don't filter by status - get all registrations (paid, sent, etc.)
      // Allow non-coaches to see registrants too (for public events)
      const token = user?.token || localStorage.getItem('token');
      console.log(`[Events] Making API call to /api/events/${eventId}/registrants`);
      const res = await axios.get(`/api/events/${eventId}/registrants`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      console.log(`[Events] API response for event ${eventId}:`, res.data);
      console.log(`[Events] Fetched ${res.data.registrants?.length || 0} registrants for event ${eventId}`);
      setTryoutRegistrants(prev => ({
        ...prev,
        [eventId]: res.data.registrants || []
      }));
    } catch (err) {
      console.error(`[Events] Failed to fetch tryout registrants for event ${eventId}:`, err);
      console.error(`[Events] Error response:`, err.response?.data);
      console.error(`[Events] Error status:`, err.response?.status);
      // Show error only if it's a real error (not 404 for non-tryout events)
      if (err.response?.status !== 404 && err.response?.status !== 403) {
        // Don't show error for 403 (unauthorized) - users might not have access to registrants
        if (isCoach) {
          toast.error('Failed to load tryout registrations');
        }
      } else {
        console.log(`[Events] Skipping error display for status ${err.response?.status}`);
      }
    }
  };

  const sendEventReminder = async (eventId) => {
    try {
      const timing = remindTimingByEvent[eventId] || 'now';
      await axios.post(`/api/emails/events/${eventId}/remind`, { timing }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Event reminder sent');
    } catch (err) {
      console.error('Failed to send event reminder', err);
      toast.error(err.response?.data?.message || 'Failed to send reminder');
    }
  };

  // RSVP functions
  const handleRsvp = async (eventId, status) => {
    try {
      const token = user?.token || localStorage.getItem('token');
      const response = await axios.post(`/api/events/${eventId}/rsvp`, 
        { status }, 
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      
      // Update local events with new RSVP data
      setEvents(prev => prev.map(event => 
        event.id === eventId 
          ? {
              ...event,
              rsvp_attending: response.data.rsvp_attending || [],
              rsvp_maybe: response.data.rsvp_maybe || [],
              rsvp_not_attending: response.data.rsvp_not_attending || []
            }
          : event
      ));
      
      // Show success message
      const statusText = status === 'attending' ? 'attending' : 
                        status === 'not_attending' ? 'not attending' : 'maybe attending';
      toast.success(`You are now ${statusText} this event`);
    } catch (error) {
      console.error('Error updating RSVP:', error);
      toast.error('Failed to update RSVP');
    }
  };

  const handleSaveGameScore = async (eventId, homeScore, awayScore) => {
    try {
      const token = user?.token || localStorage.getItem('token');
      const response = await axios.put(`/api/scheduling/games/${eventId}/score`,
        { home_score: parseInt(homeScore, 10), away_score: parseInt(awayScore, 10) },
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      setEvents(prev => prev.map(event =>
        event.id === eventId
          ? { ...event, home_score: response.data.homeScore, away_score: response.data.awayScore, winner_team_id: response.data.winnerTeamId, status: response.data.status }
          : event
      ));
      toast.success('Score saved');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save score');
    }
  };

  const getUserRsvpStatus = (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event || !user?.id) return null;
    
    if ((event.rsvp_attending || []).some(u => u.id === user.id)) return 'attending';
    if ((event.rsvp_not_attending || []).some(u => u.id === user.id)) return 'not_attending';
    if ((event.rsvp_maybe || []).some(u => u.id === user.id)) return 'maybe';
    return null;
  };

  // Fetch events from API
  const selectedTeamId = selectedTeam?.id || null;

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = user?.token || localStorage.getItem('token');
      
      // If no team selected and not showing all teams, return empty
      if (!showAllTeams && !selectedTeamId) {
        setEvents([]);
        return;
      }

      // If showing all teams, fetch events for all teams the user has access to
      if (showAllTeams) {
        // 1) Load all teams (includes parent-linked child teams)
        const teamsResp = await axios.get('/api/teams/my-teams', {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        const teams = Array.isArray(teamsResp.data) ? teamsResp.data : [];

        // 2) Fetch events for each team in parallel, plus events created by the user
        const eventPromises = teams.map(t => 
          axios.get(`/api/events?teamId=${t.id}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          })
        );
        
        // Also fetch events without teamId filter to get events the user created
        // This ensures newly created events show up even if team membership hasn't synced
        if (user?.id) {
          eventPromises.push(
            axios.get('/api/events', {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
          );
        }
        
        const eventResponses = await Promise.allSettled(eventPromises);

        // 3) Merge and annotate with team info, removing duplicates
        const merged = [];
        const eventIds = new Set(); // Track event IDs to avoid duplicates
        
        eventResponses.forEach((res, idx) => {
          if (res.status === 'fulfilled') {
            const team = idx < teams.length ? teams[idx] : null;
            const items = Array.isArray(res.value.data) ? res.value.data : [];
            items.forEach(event => {
              // Skip if we've already added this event
              if (eventIds.has(event.id)) {
                return;
              }
              eventIds.add(event.id);

              // Resolve team name from the event's own Team association first — it's
              // authoritative. /api/events?teamId=X can return events outside team X
              // (the endpoint ORs the teamId filter with broader org-wide access rules),
              // so trusting "team" (the team we queried with) mislabels those events.
              let teamName = event.team?.name;
              if (!teamName && event.team_id) {
                const eventTeam = teams.find(t => t.id === event.team_id);
                teamName = eventTeam?.name;
              }
              if (!teamName) {
                teamName = team?.name;
              }

              merged.push({
                id: event.id,
                title: event.title,
                type: event.type,
                teamId: event.team_id,
                teamName: teamName,
                team: event.team,
                date: event.date,
                time: event.time,
                duration: event.duration || 60,
                location: event.location,
                description: event.description,
                attendees: event.attendees || [],
                maxAttendees: event.maxAttendees || 20,
                isRequired: event.required_attendees && event.required_attendees.length > 0,
                createdBy: event.created_by,
                createdAt: event.created_at,
                rsvp_attending: event.rsvp_attending || [],
                rsvp_maybe: event.rsvp_maybe || [],
                rsvp_not_attending: event.rsvp_not_attending || [],
                notes: event.notes,
                isOpenRegistration: event.isOpenRegistration,
                registrationFee: event.registrationFee,
                maxSpots: event.maxSpots,
                spotsClaimed: event.spotsClaimed,
                spotsRemaining: event.spotsRemaining,
                fundraisingGoal: event.fundraisingGoal,
                amountRaised: event.amountRaised,
                donationCount: event.donationCount,
                organization_id: event.organization_id,
                age_group_id: event.age_group_id,
                ageGroup: event.ageGroup,
                address: event.address,
                away_team_id: event.away_team_id,
                away_team: event.away_team,
                home_score: event.home_score,
                away_score: event.away_score,
                winner_team_id: event.winner_team_id,
                season_phase: event.season_phase,
                facility_resource_id: event.facility_resource_id,
                facility_resource: event.facility_resource,
              });
            });
          }
        });

        setEvents(merged);
        return;
      }

      // If authenticated, fetch events for the selected team
      const teamId = selectedTeamId;
      if (!teamId) {
        setEvents([]);
        return;
      }

      const response = await axios.get(`/api/events?teamId=${teamId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      // Transform API data to match frontend format
      const transformedEvents = response.data.map(event => ({
        id: event.id,
        title: event.title,
        type: event.type,
        teamId: event.team_id,
        // The event's own Team association is authoritative — this fetch can return
        // events outside selectedTeam (see showAllTeams path above for why).
        teamName: event.team?.name || selectedTeam?.name,
        team: event.team,
        date: event.date,
        time: event.time,
        duration: event.duration || 60,
        location: event.location,
        description: event.description,
        attendees: event.attendees || [],
        maxAttendees: event.maxAttendees || 20,
        isRequired: event.required_attendees && event.required_attendees.length > 0,
        createdBy: event.created_by,
        createdAt: event.created_at,
        rsvp_attending: event.rsvp_attending || [],
        rsvp_maybe: event.rsvp_maybe || [],
        rsvp_not_attending: event.rsvp_not_attending || [],
        notes: event.notes, // Store notes to check for tryout flag
        isOpenRegistration: event.isOpenRegistration,
        registrationFee: event.registrationFee,
        maxSpots: event.maxSpots,
        spotsClaimed: event.spotsClaimed,
        spotsRemaining: event.spotsRemaining,
        fundraisingGoal: event.fundraisingGoal,
        amountRaised: event.amountRaised,
        donationCount: event.donationCount,
        organization_id: event.organization_id,
        age_group_id: event.age_group_id,
        ageGroup: event.ageGroup, // Include full age group object
        address: event.address,
        away_team_id: event.away_team_id,
        away_team: event.away_team,
        home_score: event.home_score,
        away_score: event.away_score,
        winner_team_id: event.winner_team_id,
        season_phase: event.season_phase,
        facility_resource_id: event.facility_resource_id,
        facility_resource: event.facility_resource,
      }));
      
      setEvents(transformedEvents);
      
      // Debug: Log all events to see their structure
      console.log(`[Events] Loaded ${transformedEvents.length} events`);
      transformedEvents.forEach(event => {
        console.log(`[Events] Event ${event.id} (${event.title}):`, {
          type: event.type,
          age_group_id: event.age_group_id,
          organization_id: event.organization_id,
          team_id: event.teamId,
          isOpenRegistration: event.isOpenRegistration,
          ageGroup: event.ageGroup,
          organization: event.organization
        });
      });
      
      // Auto-fetch registrants for tryout events (for all users to see total attendees)
      const tryoutEvents = transformedEvents.filter(event => {
        const isTryout = isTryoutEvent(event);
        console.log(`[Events] Checking event ${event.id} (${event.title}): isTryout=${isTryout}`);
        return isTryout;
      });
      console.log(`[Events] Found ${tryoutEvents.length} tryout events, fetching registrants...`);
      tryoutEvents.forEach(event => {
        console.log(`[Events] Auto-fetching registrants for tryout event ${event.id} (${event.title})`);
        fetchTryoutRegistrants(event.id);
      });
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to fetch events');
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  // Handle event creation
  const handleAddEvent = async (eventData) => {
    try {
      const response = await axios.post('/api/events', {
        ...eventData,
        teamId: selectedTeam?.id || 1
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      // Refresh events after creation
      await fetchEvents();
      toast.success('Event created successfully');
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [selectedTeamId, showAllTeams]);

  // Auto-show events without requiring an explicit "All Teams" click
  useEffect(() => {
    // If no team is selected, default to showing all teams so users immediately see events
    if (!selectedTeam) {
      setShowAllTeams(true);
    }
  }, [selectedTeam]);

  const handleTeamChange = (team) => {
    setSelectedTeam(prev => {
      const nextId = team?.id || null;
      if ((prev?.id || null) === nextId) {
        return prev;
      }
      return team || null;
    });
    setShowAllTeams(false); // Reset to single team view when selecting a team
  };

  const handleShowAllTeams = () => {
    setShowAllTeams(true);
    setSelectedTeam(null);
  };

  // Handle clicks outside subscription dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSubscriptionDropdown && !event.target.closest('.subscription-dropdown')) {
        setShowSubscriptionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSubscriptionDropdown]);

  const [formData, setFormData] = useState({
    title: '',
    type: 'practice',
    date: '',
    time: '',
    duration: '',
    location: '',
    description: '',
    maxAttendees: '',
    isRequired: true,
    isOpenRegistration: false,
    registrationFee: '',
    maxSpots: '',
    fundraisingGoal: '',
    organization_id: '',
    age_group_id: '',
    program_id: '',
    teamId: '', // Add teamId to form data
    repeatPracticeWeekly: false,
    recurringUntilDate: '',
    recurringDaysOfWeek: [],
    programTitle: '',
    programDescription: '',
    programAmount: '',
    programDueDate: '',
    programEndDate: '',
    programPaymentPlanEnabled: false,
    programPaymentPlanOptions: [],
    programAutoInvoiceOnAssignment: true,
  });
  const [organizations, setOrganizations] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [teams, setTeams] = useState([]); // Add teams state
  const [programs, setPrograms] = useState([]); // Active programs for the selected organization

  // Fetch organizations when needed (for tryouts)
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (formData.type === 'tryout' && (showAddForm || editingEvent)) {
        try {
          const token = user?.token || localStorage.getItem('token');
          const response = await axios.get('/api/organizations/my-organizations', {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          // Ensure we always have an array and transform the data structure
          const orgs = response.data;
          if (Array.isArray(orgs)) {
            // Transform OrganizationUser objects to plain organization objects
            const transformed = orgs.map(orgUser => {
              // Handle both structures: already transformed or with nested Organization
              if (orgUser.Organization) {
                return {
                  id: orgUser.Organization.id,
                  name: orgUser.Organization.name,
                  description: orgUser.Organization.description,
                  organizationCode: orgUser.Organization.organizationCode
                };
              } else if (orgUser.id && orgUser.name) {
                // Already in the correct format
                return orgUser;
              } else {
                // Fallback: use the orgUser itself if it has id/name
                return {
                  id: orgUser.organization_id || orgUser.id,
                  name: orgUser.name || 'Unknown Organization',
                  description: orgUser.description || '',
                  organizationCode: orgUser.organizationCode || ''
                };
              }
            });
            setOrganizations(transformed);
          } else {
            setOrganizations([]);
          }
        } catch (error) {
          console.error('Error fetching organizations:', error);
          setOrganizations([]);
        }
      }
    };
    fetchOrganizations();
  }, [formData.type, showAddForm, editingEvent, user]);

  // Fetch teams when form is shown
  useEffect(() => {
    const fetchTeams = async () => {
      if (showAddForm || editingEvent) {
        try {
          const token = user?.token || localStorage.getItem('token');
          const response = await axios.get('/api/teams/my-teams', {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          let teamsData = Array.isArray(response.data) ? response.data : [];

          // "my-teams" only returns teams the user is personally a member of, so an
          // org admin/app manager editing an auto-generated event (whose team they were
          // never added to) wouldn't see it as a selectable option. If the event we're
          // editing belongs to an organization, also pull that org's teams and merge them in.
          if (editingEvent?.organization_id) {
            try {
              const orgTeamsResp = await axios.get(`/api/teams?organizationId=${editingEvent.organization_id}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
              });
              const orgTeams = Array.isArray(orgTeamsResp.data) ? orgTeamsResp.data : [];
              const existingIds = new Set(teamsData.map(t => t.id));
              teamsData = [...teamsData, ...orgTeams.filter(t => !existingIds.has(t.id))];
            } catch (orgTeamsError) {
              console.error('Error fetching organization teams for event form:', orgTeamsError);
            }
          }

          console.log('Fetched teams for event form:', teamsData);
          setTeams(teamsData);
          
          // If no team is selected in form but selectedTeam exists, use it
          if (!formData.teamId && selectedTeam) {
            setFormData(prev => ({
              ...prev,
              teamId: selectedTeam.id.toString(),
              age_group_id: selectedTeam.age_group_id ? selectedTeam.age_group_id.toString() : ''
            }));
          }
        } catch (error) {
          console.error('Error fetching teams:', error);
          setTeams([]); // Ensure teams is set to empty array on error
        }
      } else {
        // Clear teams when form is closed
        setTeams([]);
      }
    };
    fetchTeams();
  }, [showAddForm, editingEvent, user]);

  // Fetch age groups when needed (for tryouts and other events)
  useEffect(() => {
    const fetchAgeGroups = async () => {
      if (showAddForm || editingEvent) {
        try {
          const token = user?.token || localStorage.getItem('token');
          // If organization is selected, fetch age groups for that org (includes global)
          // Otherwise, fetch global age groups
          const orgId = formData.organization_id || null;
          const url = orgId 
            ? `/api/age-groups?organizationId=${orgId}`
            : '/api/age-groups';
          
          const response = await axios.get(url, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          
          if (Array.isArray(response.data)) {
            setAgeGroups(response.data);
          } else {
            setAgeGroups([]);
          }
        } catch (error) {
          console.error('Error fetching age groups:', error);
          setAgeGroups([]);
        }
      }
    };
    fetchAgeGroups();
  }, [formData.organization_id, showAddForm, editingEvent, user]);

  // Determine the organization context for program selection (explicit org or selected team's org)
  const effectiveProgramOrgId = useMemo(() => {
    if (formData.organization_id) return parseInt(formData.organization_id, 10);
    const teamForOrg = formData.teamId
      ? teams.find(t => t.id?.toString() === formData.teamId.toString())
      : (selectedTeam || null);
    return teamForOrg?.organization_id || null;
  }, [formData.organization_id, formData.teamId, teams, selectedTeam]);

  // Fetch active programs for the org when configuring a tryout
  useEffect(() => {
    const fetchPrograms = async () => {
      if (formData.type !== 'tryout' || !(showAddForm || editingEvent) || !effectiveProgramOrgId) {
        setPrograms([]);
        return;
      }
      try {
        const token = user?.token || localStorage.getItem('token');
        const response = await axios.get(`/api/programs?organization_id=${effectiveProgramOrgId}&active=true`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        setPrograms(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching programs:', error);
        setPrograms([]);
      }
    };
    fetchPrograms();
  }, [formData.type, effectiveProgramOrgId, showAddForm, editingEvent, user]);

  const eventTypes = [
    { value: 'practice', label: 'Practice', color: 'bg-blue-100 text-blue-800' },
    { value: 'game', label: 'Game', color: 'bg-red-100 text-red-800' },
    { value: 'meeting', label: 'Meeting', color: 'bg-green-100 text-green-800' },
    { value: 'tournament', label: 'Tournament', color: 'bg-purple-100 text-purple-800' },
    { value: 'tryout', label: 'Tryout', color: 'bg-amber-100 text-amber-800' },
    { value: 'lesson', label: 'Lesson', color: 'bg-teal-100 text-teal-800' },
    { value: 'fundraiser', label: 'Fundraiser', color: 'bg-pink-100 text-pink-800' },
    { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-800' },
  ];
  const availableEventTypes = canConfigureTryoutSettings
    ? eventTypes
    : eventTypes.filter((eventType) => eventType.value !== 'tryout' || (editingEvent && formData.type === 'tryout'));

  const PLAN_OPTION_CANDIDATES = [2, 3, 4, 6, 9, 12];

  const parseLocalDate = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split('-').map((v) => parseInt(v, 10));
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  };

  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const addMonthsClamped = (baseDate, monthDelta) => {
    const result = new Date(baseDate);
    const day = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + monthDelta);
    const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(day, lastDay));
    return result;
  };

  const programDueDateObj = parseLocalDate(formData.programDueDate);
  const programEndDateObj = parseLocalDate(formData.programEndDate);
  const programPlanEndDateObj = (() => {
    if (!programDueDateObj) return null;
    if (programEndDateObj && programEndDateObj > programDueDateObj) return programEndDateObj;
    return programDueDateObj;
  })();

  const maxProgramInstallmentsAllowed = (() => {
    if (!programPlanEndDateObj) return 1;
    const today = startOfDay(new Date());
    const end = startOfDay(programPlanEndDateObj);
    if (end < today) return 1;
    let max = 1;
    for (let months = 2; months <= 12; months++) {
      const firstDueDate = addMonthsClamped(end, -(months - 1));
      if (startOfDay(firstDueDate) >= today) {
        max = months;
      } else {
        break;
      }
    }
    return max;
  })();

  const availableProgramPlanOptions = (() => {
    if (maxProgramInstallmentsAllowed < 2) return [];
    const options = PLAN_OPTION_CANDIDATES.filter(months => months <= maxProgramInstallmentsAllowed);
    if (options.length > 0) return options;
    return [maxProgramInstallmentsAllowed];
  })();

  useEffect(() => {
    if (!formData.programPaymentPlanEnabled) return;
    setFormData(prev => {
      const filtered = (prev.programPaymentPlanOptions || []).filter(months => months <= maxProgramInstallmentsAllowed && months >= 2);
      const nextOptions = filtered.length > 0 ? filtered : (availableProgramPlanOptions.length > 0 ? [availableProgramPlanOptions[0]] : []);
      if (
        filtered.length === (prev.programPaymentPlanOptions || []).length &&
        nextOptions.length === (prev.programPaymentPlanOptions || []).length
      ) {
        return prev;
      }
      return {
        ...prev,
        programPaymentPlanOptions: nextOptions
      };
    });
  }, [formData.programPaymentPlanEnabled, maxProgramInstallmentsAllowed, availableProgramPlanOptions]);

  const filteredEvents = events
    .filter(event => {
      const matchesSearch = 
        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (event.description && event.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (event.location && event.location.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesType = typeFilter === 'all' || event.type === typeFilter;
      
      const matchesTeam = teamFilter === 'all' || event.teamId === parseInt(teamFilter);
      
      // Default to future (or today) events only
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Handle different date formats from API
      // parseLocalDate avoids the UTC-midnight parsing pitfall of `new Date("YYYY-MM-DD")`,
      // which shifts the date back a day once compared/rendered in local time.
      let eventDate = (event.date && parseLocalDate(event.date)) || new Date();
      eventDate.setHours(0, 0, 0, 0);
      
      const isFutureOrToday = eventDate >= today;
      
      // Filter to un-RSVP'd events if filter is active
      let matchesRsvpFilter = true;
      if (filterUnRsvp && user) {
        const userId = user.id;
        const rsvpAttending = event.rsvp_attending || [];
        const rsvpMaybe = event.rsvp_maybe || [];
        const rsvpNotAttending = event.rsvp_not_attending || [];
        
        // Helper to check if user ID is in RSVP array (handles both ID arrays and object arrays)
        const hasUserInArray = (arr) => {
          return arr.some(item => {
            // Handle both: array of IDs or array of objects with id property
            return (typeof item === 'number' && item === userId) ||
                   (typeof item === 'object' && item !== null && item.id === userId);
          });
        };
        
        // User hasn't RSVP'd if their ID is not in any RSVP array
        const hasRsvp = hasUserInArray(rsvpAttending) || 
                       hasUserInArray(rsvpMaybe) || 
                       hasUserInArray(rsvpNotAttending);
        
        matchesRsvpFilter = !hasRsvp;
      }
      
      return matchesSearch && matchesType && matchesTeam && isFutureOrToday && matchesRsvpFilter;
    })
    // sort ascending by date/time
    .sort((a, b) => {
      const aDate = new Date(`${a.date}T${a.time || '00:00'}`);
      const bDate = new Date(`${b.date}T${b.time || '00:00'}`);
      return aDate - bDate;
    });

  // Registration / payment settings are stored as JSON in the event's notes.
  const buildEventNotes = ({ canUseTryoutFields }) => {
    const notes = {
      ...(formData.description ? { description: formData.description } : {}),
      tryout: canUseTryoutFields,
      isOpenRegistration: canUseTryoutFields && !!formData.isOpenRegistration,
      registrationFee: canUseTryoutFields && formData.registrationFee ? Number(formData.registrationFee) : 0
    };
    if (formData.type === 'lesson') {
      notes.registrationFee = formData.registrationFee ? Number(formData.registrationFee) : 0;
      notes.maxSpots = formData.maxSpots ? parseInt(formData.maxSpots, 10) : null;
    }
    if (formData.type === 'fundraiser') {
      notes.fundraisingGoal = formData.fundraisingGoal ? Number(formData.fundraisingGoal) : null;
    }
    return notes;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isTryoutTypeSelected = formData.type === 'tryout';
    const canUseTryoutFields = canConfigureTryoutSettings && isTryoutTypeSelected;
    
    try {
      if (editingEvent) {
        // Update existing event
        if (user && user.token) {
          // API call for authenticated users
          const updatePayload = {
            title: formData.title,
            type: formData.type, // Keep the type as-is, including 'tryout'
            date: formData.date,
            time: formData.time,
            location: formData.location,
            description: formData.description,
            required_attendees: formData.isRequired ? [user.id] : [],
            organization_id: (isTryoutTypeSelected || formData.organization_id) && formData.organization_id ? parseInt(formData.organization_id, 10) : undefined,
            age_group_id: formData.age_group_id ? parseInt(formData.age_group_id, 10) : undefined,
            program_id: canUseTryoutFields && formData.program_id ? parseInt(formData.program_id, 10) : undefined,
            address: formData.locationData ? {
              name: formData.locationData.name,
              address: formData.locationData.address,
              coordinates: formData.locationData.coordinates,
              type: formData.locationData.type,
              placeId: formData.locationData.placeId
            } : null,
            notes: JSON.stringify(buildEventNotes({ canUseTryoutFields: isTryoutTypeSelected }))
          };
          const updDur = parseInt(formData.duration);
          if (!Number.isNaN(updDur)) updatePayload.duration = updDur;
          const updMax = parseInt(formData.maxAttendees);
          if (!Number.isNaN(updMax)) updatePayload.maxAttendees = updMax;

          const response = await axios.put(`/api/events/${editingEvent.id}`, updatePayload, {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          });
          
          // Refresh events from server to ensure we have the complete, properly formatted event
          await fetchEvents();
          setEditingEvent(null);
          toast.success('Event updated successfully!');
        } else {
          // Local state update for demo mode
          setEvents(prev => prev.map(event => 
            event.id === editingEvent.id 
              ? { ...event, ...formData, duration: parseInt(formData.duration), maxAttendees: parseInt(formData.maxAttendees) }
              : event
          ));
          setEditingEvent(null);
          toast.success('Event updated successfully!');
        }
      } else {
        // Add new event
        if (user && user.token) {
          // API call for authenticated users
          // Get teamId from formData or selectedTeam - required for practice events
          const teamId = formData.teamId ? parseInt(formData.teamId, 10) : (selectedTeam?.id);
          const isLeagueWideTryout = isTryoutTypeSelected && (formData.organization_id || formData.age_group_id);

          if (isTryoutTypeSelected && !canConfigureTryoutSettings) {
            toast.error('Only organization admins can create tryout events.');
            return;
          }

          // For practice events, teamId is required
          if (formData.type === 'practice' && !teamId) {
            toast.error('Please select a team to create a practice event');
            return;
          }

          const shouldCreatePracticeRecurrence =
            RECURRING_EVENT_TYPES.includes(formData.type) &&
            !!formData.repeatPracticeWeekly;
          if (shouldCreatePracticeRecurrence) {
            if (!formData.recurringUntilDate) {
              toast.error(`Please choose an end date for recurring ${formData.type}s.`);
              return;
            }
            if (!Array.isArray(formData.recurringDaysOfWeek) || formData.recurringDaysOfWeek.length === 0) {
              toast.error(`Please select at least one weekday for recurring ${formData.type}s.`);
              return;
            }
            const firstPracticeDate = parseLocalDate(formData.date);
            const recurringUntilDate = parseLocalDate(formData.recurringUntilDate);
            if (!firstPracticeDate || !recurringUntilDate) {
              toast.error('Recurrence dates are invalid.');
              return;
            }
            if (recurringUntilDate <= firstPracticeDate) {
              toast.error(`Recurring end date must be after the first ${formData.type} date.`);
              return;
            }
          }

          // For league/age-group tryouts, team is optional (event is for whole org or age group)
          if (canUseTryoutFields && !teamId && !formData.organization_id && !formData.age_group_id) {
            toast.error('For a league-wide or age-group tryout, please select an organization or age group.');
            return;
          }

          // New tryouts must be associated with a program (program defines season dates + fees).
          if (canUseTryoutFields && !formData.program_id) {
            toast.error('Please select a program for this tryout.');
            return;
          }

          const notesPayload = buildEventNotes({ canUseTryoutFields });
          const programAmountNumber = parseFloat(formData.programAmount);
          if (
            canUseTryoutFields &&
            Number.isFinite(programAmountNumber) &&
            programAmountNumber > 0
          ) {
            notesPayload.programAmount = programAmountNumber;
            notesPayload.programTitle = formData.programTitle?.trim() || `${formData.title} Program`;
            notesPayload.programDescription = formData.programDescription?.trim() || '';
            notesPayload.programDueDate = formData.programDueDate || null;
            notesPayload.programEndDate = formData.programEndDate || null;
            notesPayload.programPaymentPlanEnabled = !!formData.programPaymentPlanEnabled;
            notesPayload.programPaymentPlanOptions = formData.programPaymentPlanEnabled
              ? (formData.programPaymentPlanOptions || [])
              : [];
            notesPayload.programAutoInvoiceOnAssignment = formData.programAutoInvoiceOnAssignment !== false;
          }

          const createPayload = {
            title: formData.title,
            type: formData.type, // Keep the type as-is, including 'tryout'
            date: formData.date,
            time: formData.time,
            location: formData.location,
            description: formData.description,
            required_attendees: formData.isRequired ? [user.id] : [],
            ...(isLeagueWideTryout ? {} : { teamId: teamId ?? undefined }), // Omit teamId for league-wide tryouts so event isn't tied to a team
            organization_id: (isTryoutTypeSelected || formData.organization_id) && formData.organization_id ? parseInt(formData.organization_id, 10) : undefined,
            age_group_id: formData.age_group_id ? parseInt(formData.age_group_id, 10) : undefined,
            program_id: canUseTryoutFields && formData.program_id ? parseInt(formData.program_id, 10) : undefined,
            address: formData.locationData ? {
              name: formData.locationData.name,
              address: formData.locationData.address,
              coordinates: formData.locationData.coordinates,
              type: formData.locationData.type,
              placeId: formData.locationData.placeId
            } : null,
            notes: JSON.stringify(notesPayload)
          };
          if (shouldCreatePracticeRecurrence) {
            createPayload.is_recurring = true;
            createPayload.recurring_pattern = {
              frequency: 'weekly',
              interval: 1,
              until: formData.recurringUntilDate,
              daysOfWeek: formData.recurringDaysOfWeek
            };
          } else if (RECURRING_EVENT_TYPES.includes(formData.type)) {
            createPayload.is_recurring = false;
            createPayload.recurring_pattern = null;
          }
          const crtDur = parseInt(formData.duration);
          if (!Number.isNaN(crtDur)) createPayload.duration = crtDur;
          const crtMax = parseInt(formData.maxAttendees);
          if (!Number.isNaN(crtMax)) createPayload.maxAttendees = crtMax;

          const response = await axios.post('/api/events', createPayload, {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          });

          const createdEventId = response?.data?.event?.id || response?.data?.id;
          if (
            canUseTryoutFields &&
            Number.isFinite(programAmountNumber) &&
            programAmountNumber > 0
          ) {
            const selectedTeamData = formData.teamId
              ? teams.find(t => t.id.toString() === formData.teamId)
              : (selectedTeam || null);
            const teamIdForFeeConfig = formData.teamId
              ? parseInt(formData.teamId, 10)
              : (selectedTeamData?.id || null);
            const ageGroupIdForFeeConfig = formData.age_group_id
              ? parseInt(formData.age_group_id, 10)
              : null;

            if (!teamIdForFeeConfig && !ageGroupIdForFeeConfig) {
              toast.error('Tryout created, but program invoice setup needs a team or age group.');
            } else {
              // For league/age-group tryouts, prefer age-group scoped program fee configs.
              // Team can be preselected in the form context, but billing should apply across teams.
              const shouldUseAgeGroupScope =
                !!ageGroupIdForFeeConfig && !!isLeagueWideTryout;

              const configPayload = {
                title: formData.programTitle?.trim() || `${formData.title} Program`,
                description: formData.programDescription?.trim() || null,
                amount: programAmountNumber,
                season: selectedTeamData?.season || null,
                due_date: formData.programDueDate || null,
                program_end_date: formData.programEndDate || null,
                payment_plan_enabled: !!formData.programPaymentPlanEnabled,
                payment_plan_options: formData.programPaymentPlanEnabled
                  ? (formData.programPaymentPlanOptions || [])
                  : [],
                auto_invoice_on_assignment: formData.programAutoInvoiceOnAssignment !== false
              };

              if (shouldUseAgeGroupScope) {
                configPayload.age_group_id = ageGroupIdForFeeConfig;
                configPayload.organization_id = formData.organization_id
                  ? parseInt(formData.organization_id, 10)
                  : (selectedTeamData?.organization_id || undefined);
              } else if (teamIdForFeeConfig) {
                configPayload.team_id = teamIdForFeeConfig;
              } else {
                configPayload.age_group_id = ageGroupIdForFeeConfig;
                configPayload.organization_id = formData.organization_id
                  ? parseInt(formData.organization_id, 10)
                  : (selectedTeamData?.organization_id || undefined);
              }

              try {
                await axios.post('/api/program-fees', configPayload, {
                  headers: {
                    'Authorization': `Bearer ${user.token}`
                  }
                });
                toast.success(
                  createdEventId
                    ? `Tryout created and program invoice automation enabled (event #${createdEventId}).`
                    : 'Tryout created and program invoice automation enabled.'
                );
              } catch (programFeeError) {
                console.error('Error creating program fee config:', programFeeError);
                toast.error(programFeeError.response?.data?.message || 'Tryout created, but program invoice setup failed.');
              }
            }
          }
          
          // Refresh events from server to ensure we have the complete, properly formatted event
          await fetchEvents();
          if (!(canUseTryoutFields && formData.programAmount && parseFloat(formData.programAmount) > 0)) {
            const recurringInstancesCreated = parseInt(response?.data?.recurring_instances_created || 0, 10);
            if (Number.isInteger(recurringInstancesCreated) && recurringInstancesCreated > 0) {
              toast.success(`${formData.type === 'lesson' ? 'Lesson' : 'Practice'} series created (${recurringInstancesCreated + 1} total events).`);
            } else {
              toast.success('Event created successfully!');
            }
          }
        } else {
          // Local state update for demo mode
          const newEvent = {
            id: Date.now(),
            ...formData,
            duration: parseInt(formData.duration),
            maxAttendees: parseInt(formData.maxAttendees),
            attendees: [],
            createdBy: user?.name || 'Demo User',
            createdAt: new Date().toISOString().split('T')[0],
          };
          setEvents(prev => [...prev, newEvent]);
          toast.success('Event created successfully!');
        }
      }
      
      setFormData({
        title: '',
        type: 'practice',
        date: '',
        time: '',
        duration: '',
        location: '',
        locationData: null,
        description: '',
        maxAttendees: '',
        isRequired: true,
        isOpenRegistration: false,
        registrationFee: '',
        maxSpots: '',
        fundraisingGoal: '',
        organization_id: '',
        age_group_id: '',
        program_id: '',
        teamId: selectedTeam?.id ? selectedTeam.id.toString() : '', // Pre-populate with selectedTeam if available
        repeatPracticeWeekly: false,
        recurringUntilDate: '',
        recurringDaysOfWeek: [],
        programTitle: '',
        programDescription: '',
        programAmount: '',
        programDueDate: '',
        programEndDate: '',
        programPaymentPlanEnabled: false,
        programPaymentPlanOptions: [],
        programAutoInvoiceOnAssignment: true,
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(error.response?.data?.message || 'Failed to save event');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      type: event.type,
      date: event.date,
      time: event.time,
      duration: event.duration.toString(),
      // Auto-generated practices/games have no free-text location, only a booked
      // facility resource — fall back to its name (plus its park) so it's visible/editable here too.
      location: event.location || (event.facility_resource
        ? (event.facility_resource.facility?.name
          ? `${event.facility_resource.name} — ${event.facility_resource.facility.name}`
          : event.facility_resource.name)
        : ''),
      description: event.description,
      maxAttendees: event.maxAttendees.toString(),
      isRequired: event.isRequired,
      locationData: event.address || null,
      organization_id: event.organization_id || '',
      age_group_id: event.age_group_id || '',
      program_id: event.program_id ? event.program_id.toString() : '',
      teamId: event.team_id ? event.team_id.toString() : '',
      isOpenRegistration: event.isOpenRegistration || false,
      registrationFee: event.registrationFee || '',
      maxSpots: event.maxSpots ? event.maxSpots.toString() : '',
      fundraisingGoal: event.fundraisingGoal ? event.fundraisingGoal.toString() : '',
      repeatPracticeWeekly: false,
      recurringUntilDate: '',
      recurringDaysOfWeek: [],
      programTitle: '',
      programDescription: '',
      programAmount: '',
      programDueDate: '',
      programEndDate: '',
      programPaymentPlanEnabled: false,
      programPaymentPlanOptions: [],
      programAutoInvoiceOnAssignment: true,
    });
    setShowAddForm(true);
  };

  const handleDelete = (eventId) => {
    setConfirmState({
      title: 'Delete Event',
      message: 'Are you sure you want to delete this event?',
      isDangerous: true,
      onConfirm: async () => {
        try {
          if (user && user.token) {
            await axios.delete(`/api/events/${eventId}`, {
              headers: { 'Authorization': `Bearer ${user.token}` },
            });
          }
          setEvents(prev => prev.filter(event => event.id !== eventId));
          toast.success('Event deleted successfully!');
        } catch (error) {
          console.error('Error deleting event:', error);
          toast.error('Failed to delete event');
        }
      },
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'type') {
      if (value === 'tryout' && !canConfigureTryoutSettings) {
        toast.error('Only organization admins can create tryout events.');
        return;
      }
      const isTryout = value === 'tryout';
      const isLesson = value === 'lesson';
      const isRecurringType = RECURRING_EVENT_TYPES.includes(value);
      setFormData(prev => ({
        ...prev,
        type: value,
        ...(!isTryout && !isLesson ? { registrationFee: '' } : {}),
        ...(!isLesson ? { maxSpots: '' } : {}),
        ...(value !== 'fundraiser' ? { fundraisingGoal: '' } : {}),
        ...(!isTryout ? {
          isOpenRegistration: false,
          organization_id: '',
          programTitle: '',
          programDescription: '',
          programAmount: '',
          programDueDate: '',
          programEndDate: '',
          programPaymentPlanEnabled: false,
          programPaymentPlanOptions: [],
          programAutoInvoiceOnAssignment: true
        } : {}),
        ...(!isRecurringType ? {
          repeatPracticeWeekly: false,
          recurringUntilDate: '',
          recurringDaysOfWeek: []
        } : {})
      }));
      return;
    }

    if (name === 'repeatPracticeWeekly') {
      const selectedDate = parseLocalDate(formData.date);
      const defaultDay = selectedDate ? selectedDate.getDay() : null;
      setFormData(prev => ({
        ...prev,
        repeatPracticeWeekly: checked,
        recurringUntilDate: checked ? prev.recurringUntilDate : '',
        recurringDaysOfWeek: checked
          ? (prev.recurringDaysOfWeek.length > 0
            ? prev.recurringDaysOfWeek
            : (defaultDay !== null ? [defaultDay] : []))
          : []
      }));
      return;
    }

    if (name === 'date' && formData.repeatPracticeWeekly) {
      const nextDate = parseLocalDate(value);
      setFormData(prev => ({
        ...prev,
        date: value,
        recurringDaysOfWeek: prev.recurringDaysOfWeek.length > 0
          ? prev.recurringDaysOfWeek
          : (nextDate ? [nextDate.getDay()] : [])
      }));
      return;
    }
    
    // If team is selected, auto-populate age_group_id from that team
    if (name === 'teamId' && value) {
      const selectedTeamData = teams.find(t => t.id.toString() === value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        age_group_id: selectedTeamData?.age_group_id ? selectedTeamData.age_group_id.toString() : prev.age_group_id,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const toggleRecurringDay = (dayValue) => {
    setFormData(prev => {
      const alreadySelected = prev.recurringDaysOfWeek.includes(dayValue);
      const nextDays = alreadySelected
        ? prev.recurringDaysOfWeek.filter(day => day !== dayValue)
        : [...prev.recurringDaysOfWeek, dayValue].sort((a, b) => a - b);
      return {
        ...prev,
        recurringDaysOfWeek: nextDays
      };
    });
  };

  const toggleAttendance = async (eventId, attendeeName) => {
    try {
      if (user && user.token) {
        // API call for authenticated users
        const event = events.find(e => e.id === eventId);
        const isAttending = event.attendees.includes(attendeeName);
        
        if (isAttending) {
          await axios.delete(`/api/events/${eventId}/attendees/${user.id}`, {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          });
        } else {
          await axios.post(`/api/events/${eventId}/attendees`, {
            attendeeId: user.id
          }, {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          });
        }
      }
      
      // Update local state
      setEvents(prev => prev.map(event => {
        if (event.id === eventId) {
          const isAttending = event.attendees.includes(attendeeName);
          if (isAttending) {
            return { ...event, attendees: event.attendees.filter(name => name !== attendeeName) };
          } else {
            return { ...event, attendees: [...event.attendees, attendeeName] };
          }
        }
        return event;
      }));
    } catch (error) {
      console.error('Error toggling attendance:', error);
      toast.error('Failed to update attendance');
    }
  };

  const getEventTypeColor = (type) => {
    const eventType = eventTypes.find(et => et.value === type);
    return eventType ? eventType.color : 'bg-gray-100 text-gray-800';
  };

  const getEventTypeLabel = (type) => {
    const eventType = eventTypes.find(et => et.value === type);
    return eventType ? eventType.label : 'Unknown';
  };

  const formatDate = (dateString) => {
    // Date-only strings ("YYYY-MM-DD") are parsed as UTC midnight by `new Date(...)`,
    // which shows as the previous day once rendered in a negative-UTC-offset timezone.
    // Parse as local calendar date instead.
    const date = parseLocalDate(dateString);
    if (!date) return dateString;
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getEventsForDate = (date) => {
    return filteredEvents.filter(event => event.date === date.toISOString().split('T')[0]);
  };

  const generateCalendarDays = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= lastDay || days.length < 42) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  // Calendar subscription functions
  const generateICSContent = () => {
    const events = filteredEvents.map(event => {
      const startDate = new Date(`${event.date}T${event.time}`);
      const endDate = new Date(startDate.getTime() + event.duration * 60000);
      const locationText = getEventLocationText(event);
      const mapUrl = getEventMapUrl(event);
      
      return [
        'BEGIN:VEVENT',
        `UID:${event.id}@teamcentral.com`,
        `DTSTART:${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
        `DTEND:${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:${event.description}`,
        `LOCATION:${locationText || event.location || ''}`,
        mapUrl ? `URL:${mapUrl}` : null,
        'END:VEVENT'
      ].filter(Boolean).join('\r\n');
    }).join('\r\n');

    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//FieldHouse//Team Calendar//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      events,
      'END:VCALENDAR'
    ].join('\r\n');
  };

  const downloadICS = () => {
    const icsContent = generateICSContent();
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'team-events.ics';
    link.click();
  };

  const getGoogleCalendarUrl = () => {
    // Create a single calendar subscription URL with all events
    const events = filteredEvents.map(event => {
      const startDate = new Date(`${event.date}T${event.time}`);
      const endDate = new Date(startDate.getTime() + event.duration * 60000);
      
      return {
        text: event.title,
        dates: `${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z/${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
        details: event.description,
        location: getEventLocationText(event) || event.location
      };
    });
    
    // For Google Calendar, we'll create a single subscription
    if (events.length === 0) return null;
    
    // Use the first event as the main calendar entry
    const mainEvent = events[0];
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `FieldHouse - ${mainEvent.text}`,
      dates: mainEvent.dates,
      details: `Team Calendar\n\n${events.map(e => `• ${e.text} - ${e.details}`).join('\n')}`,
      location: mainEvent.location
    });
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const getOutlookCalendarUrl = () => {
    // Create a single calendar subscription URL with all events
    const events = filteredEvents.map(event => {
      const startDate = new Date(`${event.date}T${event.time}`);
      const endDate = new Date(startDate.getTime() + event.duration * 60000);
      
      return {
        subject: event.title,
        startdt: startDate.toISOString(),
        enddt: endDate.toISOString(),
        body: event.description,
        location: getEventLocationText(event) || event.location
      };
    });
    
    // For Outlook, we'll create a single subscription
    if (events.length === 0) return null;
    
    // Use the first event as the main calendar entry
    const mainEvent = events[0];
    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      subject: `FieldHouse - ${mainEvent.subject}`,
      startdt: mainEvent.startdt,
      enddt: mainEvent.enddt,
      body: `Team Calendar\n\n${events.map(e => `• ${e.subject} - ${e.body}`).join('\n')}`,
      location: mainEvent.location
    });
    
    return `https://outlook.live.com/calendar/0/${params.toString()}`;
  };

  const openCalendarSubscription = (type) => {
    if (type === 'google') {
      const url = getGoogleCalendarUrl();
      if (url) {
        window.open(url, '_blank');
      }
    } else if (type === 'outlook') {
      const url = getOutlookCalendarUrl();
      if (url) {
        window.open(url, '_blank');
      }
    } else if (type === 'ics') {
      downloadICS();
    }
  };

  const subscribeLiveICS = async () => {
    try {
      const teamId = selectedTeamId;
      if (!teamId) {
        toast.error('Select a team first');
        return;
      }
      const token = user?.token || localStorage.getItem('token');
      const resp = await axios.get(`/api/teams/${teamId}/calendar-link`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const { url, webcalUrl } = resp.data || {};
      if (url) {
        await navigator.clipboard.writeText(url).catch(() => {});
        toast.success('Subscription link copied to clipboard');
      }
      if (webcalUrl) {
        // Attempt to open in the default calendar app
        window.location.href = webcalUrl;
      }
    } catch (e) {
      console.error('Failed to get subscription link', e);
      toast.error('Failed to open subscription link');
    }
  };

  const calendarDays = generateCalendarDays();

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading events...</p>
        </div>
      </div>
    );
  }

  // No team selected state
  if (!selectedTeam && !showAllTeams) {
    return (
      <div className="text-center py-12">
        <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Select a Team</h3>
        <p className="mt-1 text-sm text-gray-500">
          Choose a team from the dropdown above to view and manage events, or click "Show All Teams" to see events from all your teams.
        </p>
        <button
          onClick={handleShowAllTeams}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Show All Teams
        </button>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Events</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchEvents}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Team Calendar
              {filterUnRsvp && (
                <span className="ml-2 text-sm font-normal text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  Events Needing RSVP
                </span>
              )}
            </h1>
            <p className="text-gray-600 mt-1">
              {filterUnRsvp 
                ? 'Showing future events that need your RSVP'
                : showAllTeams 
                  ? 'Viewing events from all teams'
                  : selectedTeam 
                    ? `Managing events for ${selectedTeam.name} (${selectedTeam.sport} • ${selectedTeam.ageGroup})`
                    : 'Select a team to view events'
              }
            </p>
          </div>
          <div className="flex items-center space-x-3 flex-wrap w-full sm:w-auto">
            {/* Team Selector */}
            <div className="w-full sm:w-auto">
              <TeamSelector onTeamChange={handleTeamChange} allowAllOption onShowAll={handleShowAllTeams} />
            </div>
            
            {/* Show All Teams Toggle */}
            {/* Show All Teams toggle removed; default is all teams */}
            
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Calendar
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                List
              </button>
            </div>

            {/* Mobile Filters Toggle */}
            <button
              onClick={() => setShowMobileFilters(prev => !prev)}
              className="sm:hidden inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700"
            >
              <FunnelIcon className="h-4 w-4 mr-2" /> Filters
            </button>
            
            {/* Mobile Calendar Subscription Dropdown */}
            <div className="relative subscription-dropdown sm:hidden">
              <button
                onClick={() => setShowSubscriptionDropdown(!showSubscriptionDropdown)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700"
              >
                <ShareIcon className="h-4 w-4 mr-2" />
                Subscribe
              </button>
              
              {showSubscriptionDropdown && (
                <>
                  {/* Backdrop */}
                  <div 
                    className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() => setShowSubscriptionDropdown(false)}
                  ></div>
                  
                  {/* Centered Modal */}
                  <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
                    <div className="w-full max-w-sm rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-5">
                      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Subscribe to Calendar</h3>
                        <button
                          onClick={() => setShowSubscriptionDropdown(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        <button
                          onClick={() => {
                            subscribeLiveICS();
                            setShowSubscriptionDropdown(false);
                          }}
                          className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 border-b border-gray-100"
                          role="menuitem"
                        >
                          <div className="flex items-center">
                            <div className="w-4 h-4 bg-green-600 rounded mr-3"></div>
                            <div>
                              <div className="font-medium">Subscribe (Live)</div>
                              <div className="text-xs text-gray-500">Use ICS feed (auto-updates)</div>
                            </div>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            openCalendarSubscription('google');
                            setShowSubscriptionDropdown(false);
                          }}
                          className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 border-b border-gray-100"
                          role="menuitem"
                        >
                          <div className="flex items-center">
                            <div className="w-4 h-4 bg-red-500 rounded mr-3"></div>
                            <div>
                              <div className="font-medium">Google Calendar</div>
                              <div className="text-xs text-gray-500">Add events to Google Calendar</div>
                            </div>
                          </div>
                        </button>
                        
                        <button
                          onClick={() => {
                            openCalendarSubscription('outlook');
                            setShowSubscriptionDropdown(false);
                          }}
                          className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 border-b border-gray-100"
                          role="menuitem"
                        >
                          <div className="flex items-center">
                            <div className="w-4 h-4 bg-blue-500 rounded mr-3"></div>
                            <div>
                              <div className="font-medium">Outlook Calendar</div>
                              <div className="text-xs text-gray-500">Add events to Outlook</div>
                            </div>
                          </div>
                        </button>
                        
                        <button
                          onClick={() => {
                            openCalendarSubscription('ics');
                            setShowSubscriptionDropdown(false);
                          }}
                          className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                          role="menuitem"
                        >
                          <div className="flex items-center">
                            <div className="w-4 h-4 bg-gray-500 rounded mr-3"></div>
                            <div>
                              <div className="font-medium">Download ICS File</div>
                              <div className="text-xs text-gray-500">Import to any calendar app</div>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Calendar Subscription Dropdown */}
            <div className="relative subscription-dropdown hidden sm:block">
              <button
                onClick={() => setShowSubscriptionDropdown(!showSubscriptionDropdown)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <ShareIcon className="h-4 w-4 mr-2" />
                Subscribe
              </button>
              
              {showSubscriptionDropdown && (
                <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    <button
                      onClick={() => {
                        subscribeLiveICS();
                        setShowSubscriptionDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      role="menuitem"
                    >
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-green-600 rounded mr-3"></div>
                        <div>
                          <div className="font-medium">Subscribe (Live)</div>
                          <div className="text-xs text-gray-500">Use ICS feed (auto-updates)</div>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        openCalendarSubscription('google');
                        setShowSubscriptionDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      role="menuitem"
                    >
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-red-500 rounded mr-3"></div>
                        <div>
                          <div className="font-medium">Google Calendar</div>
                          <div className="text-xs text-gray-500">Add events to Google Calendar</div>
                        </div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        openCalendarSubscription('outlook');
                        setShowSubscriptionDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      role="menuitem"
                    >
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-blue-500 rounded mr-3"></div>
                        <div>
                          <div className="font-medium">Outlook Calendar</div>
                          <div className="text-xs text-gray-500">Add events to Outlook</div>
                        </div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        openCalendarSubscription('ics');
                        setShowSubscriptionDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      role="menuitem"
                    >
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-gray-500 rounded mr-3"></div>
                        <div>
                          <div className="font-medium">Download ICS File</div>
                          <div className="text-xs text-gray-500">Import to any calendar app</div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {(isCoach || !user) && (
              <button
                onClick={() => setShowAddForm(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Add Event</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        {/* Desktop/Tablet filters */}
        <div className="hidden sm:flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search events by title, description, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Type:</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Types</option>
              {eventTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          {showAllTeams && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Team:</span>
              <select
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Teams</option>
                <option value="1">Demo Braves</option>
                <option value="2">Demo Padres</option>
              </select>
            </div>
          )}
        </div>

        {/* Mobile filters panel */}
        <div className={`sm:hidden mt-2 ${showMobileFilters ? 'block' : 'hidden'}`}>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Search</label>
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  placeholder="Title, description, or location"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Types</option>
                  {eventTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              {showAllTeams && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Team</label>
                  <select
                    value={teamFilter}
                    onChange={(e) => setTeamFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All Teams</option>
                    <option value="1">Demo Braves</option>
                    <option value="2">Demo Padres</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="text-sm text-gray-600">
                {showAllTeams 
                  ? `${filteredEvents.length} total events`
                  : `${filteredEvents.length} events for ${selectedTeam?.name}`
                }
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="bg-gray-50 p-2 text-center text-sm font-medium text-gray-700">
                {day}
              </div>
            ))}

            {/* Calendar Days */}
            {calendarDays.map((date, index) => {
              const dayEvents = getEventsForDate(date);
              const isCurrentMonth = date.getMonth() === selectedDate.getMonth();
              const isToday = date.toDateString() === new Date().toDateString();
              
              return (
                <div
                  key={index}
                  className={`min-h-24 bg-white p-2 ${
                    !isCurrentMonth ? 'text-gray-400' : ''
                  } ${isToday ? 'bg-blue-50' : ''}`}
                >
                  <div className={`text-sm font-medium mb-1 ${
                    isToday ? 'text-blue-600' : ''
                  }`}>
                    {date.getDate()}
                  </div>
                  
                  {/* Events for this day */}
                  <div className="space-y-1">
                    {dayEvents.slice(0, 2).map(event => (
                      <div
                        key={event.id}
                        onClick={() => setShowEventDetails(event.id)}
                        className={`text-xs p-1 rounded cursor-pointer truncate ${
                          event.type === 'game' ? 'bg-red-100 text-red-800' :
                          event.type === 'practice' ? 'bg-blue-100 text-blue-800' :
                          event.type === 'meeting' ? 'bg-green-100 text-green-800' :
                          event.type === 'lesson' ? 'bg-teal-100 text-teal-800' :
                          event.type === 'fundraiser' ? 'bg-pink-100 text-pink-800' :
                          'bg-gray-100 text-gray-800'
                        }`}
                        title={`${event.title}${showAllTeams && event.teamName ? ` (${event.teamName})` : ''}${event.facility_resource ? ` @ ${event.facility_resource.name}` : ''}`}
                      >
                        {event.title}
                        {event.facility_resource && (
                          <span className="block text-[10px] opacity-75 truncate">📍 {event.facility_resource.name}</span>
                        )}
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-xs text-gray-500 text-center">
                        +{dayEvents.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {/* View Summary */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium">
                  {showAllTeams 
                    ? `Showing ${filteredEvents.length} events from all teams`
                    : `Showing ${filteredEvents.length} events for ${selectedTeam?.name}`
                  }
                </span>
                {showAllTeams && teamFilter !== 'all' && (
                  <span className="ml-2 text-gray-500">
                    (filtered by {teamFilter === '1' ? 'Demo Braves' : 'Demo Padres'})
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500">
                {filteredEvents.length > 0 && (
                  <span>
                    {filteredEvents.filter(e => e.type === 'game').length} games, 
                    {filteredEvents.filter(e => e.type === 'practice').length} practices,
                    {filteredEvents.filter(e => e.type === 'meeting').length} meetings
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {filteredEvents.map((event) => (
            <div key={event.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEventTypeColor(event.type)}`}>
                      {getEventTypeLabel(event.type)}
                    </span>
                    <h3 className="text-lg font-medium text-gray-900">{event.title}</h3>
                    {event.isRequired && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Required
                      </span>
                    )}
                    {/* Team indicator when showing all teams */}
                    {showAllTeams && event.teamName && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {event.teamName}
                      </span>
                    )}
                    {/* Age Group indicator */}
                    {event.ageGroup && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {event.ageGroup.name}
                      </span>
                    )}
                    {/* Booked facility resource (field/cage) indicator */}
                    {event.facility_resource && (
                      event.facility_resource.facility?.id ? (
                        <Link
                          to={`/facility/${event.facility_resource.facility.id}`}
                          onClick={e => e.stopPropagation()}
                          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 hover:bg-teal-200"
                        >
                          <MapPinIcon className="h-3 w-3" />
                          {event.facility_resource.name}
                          {` — ${event.facility_resource.facility.name}`}
                        </Link>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                          <MapPinIcon className="h-3 w-3" />
                          {event.facility_resource.name}
                        </span>
                      )
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="h-4 w-4" />
                      <span>{formatDate(event.date)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="h-4 w-4" />
                      <span>{formatTime(event.time)} ({event.duration} min)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MapPinIcon className="h-4 w-4" />
                      {(() => {
                        const locationText = getEventLocationText(event);
                        const mapUrl = getEventMapUrl(event);
                        if (!locationText) return <span>{event.location}</span>;
                        return (
                          <a
                            href={mapUrl || '#'}
                            onClick={(e) => { if (!mapUrl) e.preventDefault(); }}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {locationText}
                          </a>
                        );
                      })()}
                    </div>
                    {event.ageGroup && (
                      <div className="flex items-center space-x-2 col-span-full">
                        <span className="text-xs text-gray-500">Age Group:</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {event.ageGroup.name}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-gray-700 mb-3">{event.description}</p>
                  
                  {/* Tryout Registration Count (for coaches) */}
                  {isTryoutEvent(event) && isCoach && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-amber-900">Tryout Registrations:</span>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-semibold text-amber-700">
                            {tryoutRegistrants[event.id]?.length || 'Loading...'} registered
                          </span>
                          {!tryoutRegistrants[event.id] && (
                            <button
                              onClick={() => fetchTryoutRegistrants(event.id)}
                              className="text-xs text-amber-700 hover:text-amber-900 underline"
                            >
                              Load registrations
                            </button>
                          )}
                        </div>
                      </div>
                      {tryoutRegistrants[event.id]?.length > 0 && (
                        <div className="mt-2">
                          <a
                            href={`/tryout-assignment?eventId=${event.id}`}
                            className="text-xs text-amber-700 hover:text-amber-900 underline"
                          >
                            View and assign registrants →
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* RSVP & Registrants Section */}
                  <div className="mb-4">
                    {event.type === 'lesson' ? (
                      <LessonSignupPanel
                        event={event}
                        onClaimSpot={() => handleRegisterForEvent(event.id)}
                        onWalkUp={canAccessWalkUpRegistration() ? () => openWalkUpRegistration(event.id) : null}
                      />
                    ) : event.type === 'fundraiser' ? (
                      <FundraiserPanel
                        event={event}
                        onDonate={() => handleRegisterForEvent(event.id)}
                        canViewDonors={canShareEventLink()}
                        authToken={user?.token}
                      />
                    ) : isTryoutEvent(event) ? (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <UserGroupIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {(() => {
                                const registrants = tryoutRegistrants[event.id] || [];
                                const totalRegistered = registrants.length;
                                return (
                                  <span>
                                    <span className="font-medium text-gray-900">{totalRegistered}</span> registered
                                  </span>
                                );
                              })()}
                            </span>
                          </div>
                          {event.maxAttendees && (
                            <span className="text-sm text-gray-500">
                              Max: {event.maxAttendees}
                            </span>
                          )}
                        </div>
                        
                        {/* Registration Button for Tryouts */}
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleRegisterForEvent(event.id)}
                            className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                          >
                            Register for Tryout
                          </button>
                          {canAccessWalkUpRegistration() && (
                            <button
                              onClick={() => openWalkUpRegistration(event.id)}
                              className="px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors"
                            >
                              Walk-Up Registration
                            </button>
                          )}
                        </div>
                        
                        {/* Show Registrants for Tryouts */}
                        {(tryoutRegistrants[event.id]?.length || 0) > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs text-gray-600 mb-2">Registrants:</div>
                            <div className="space-y-2">
                              <div>
                                <div className="text-xs font-medium text-amber-700 mb-1">
                                  Registered ({tryoutRegistrants[event.id].length}):
                                  {(() => {
                                    const guestCount = (tryoutRegistrants[event.id] || []).filter(r => r.isGuest || r.player?.isGuest).length;
                                    return guestCount > 0 ? (
                                      <span className="ml-2 text-xs text-blue-600">
                                        ({guestCount} guest{guestCount !== 1 ? 's' : ''})
                                      </span>
                                    ) : null;
                                  })()}
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {(tryoutRegistrants[event.id] || []).slice(0, 10).map((reg, index) => {
                                    const displayName = reg.player?.name || reg.user?.name || 'Unknown';
                                    const isGuest = reg.isGuest || reg.player?.isGuest;
                                    return (
                                      <span key={reg.invoiceId || index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                        {displayName}
                                        {isGuest && <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 px-1 rounded">Guest</span>}
                                      </span>
                                    );
                                  })}
                                  {(tryoutRegistrants[event.id] || []).length > 10 && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                      +{tryoutRegistrants[event.id].length - 10} more
                                    </span>
                                  )}
                                </div>
                                {isCoach && tryoutRegistrants[event.id].length > 0 && (
                                  <div className="mt-1">
                                    <a
                                      href={`/tryout-assignment?eventId=${event.id}`}
                                      className="text-xs text-amber-700 hover:text-amber-900 underline"
                                    >
                                      View all registrants →
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Practice & Game Events - RSVP Only */
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <UserGroupIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {(() => {
                                const rsvpAttending = event.rsvp_attending?.length || 0;
                                const rsvpMaybe = event.rsvp_maybe?.length || 0;
                                const rsvpNotAttending = event.rsvp_not_attending?.length || 0;
                                return (
                                  <span>
                                    <span className="font-medium text-gray-900">{rsvpAttending}</span> attending, 
                                    <span className="ml-1">{rsvpMaybe} maybe, {rsvpNotAttending} not attending</span>
                                  </span>
                                );
                              })()}
                            </span>
                          </div>
                          {event.maxAttendees && (
                            <span className="text-sm text-gray-500">
                              Max: {event.maxAttendees}
                            </span>
                          )}
                        </div>
                        
                        {/* RSVP Buttons for Practices and Games */}
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">RSVP:</span>
                          <button
                            onClick={() => handleRsvp(event.id, 'attending')}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                              getUserRsvpStatus(event.id) === 'attending'
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-green-700'
                            }`}
                          >
                            ✓ Attending
                          </button>
                          <button
                            onClick={() => handleRsvp(event.id, 'maybe')}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                              getUserRsvpStatus(event.id) === 'maybe'
                                ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-yellow-50 hover:text-yellow-700'
                            }`}
                          >
                            ? Maybe
                          </button>
                          <button
                            onClick={() => handleRsvp(event.id, 'not_attending')}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                              getUserRsvpStatus(event.id) === 'not_attending'
                                ? 'bg-red-100 text-red-800 border border-red-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-700'
                            }`}
                          >
                            ✗ Not Attending
                          </button>
                        </div>
                        
                        {/* Show RSVP Lists for Practices and Games */}
                        {(event.rsvp_attending?.length > 0 || event.rsvp_maybe?.length > 0 || event.rsvp_not_attending?.length > 0) && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs text-gray-600 mb-2">RSVP Status:</div>
                            <div className="space-y-2">
                              {/* RSVP Attending */}
                              {event.rsvp_attending?.length > 0 && (
                                <div>
                                  <div className="text-xs font-medium text-green-700 mb-1">
                                    Attending ({event.rsvp_attending.length}):
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {(event.rsvp_attending || []).map((user, index) => (
                                      <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        {user.name || `User ${user.id}`}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* RSVP Maybe */}
                              {event.rsvp_maybe?.length > 0 && (
                                <div>
                                  <div className="text-xs font-medium text-yellow-700 mb-1">Maybe ({event.rsvp_maybe.length}):</div>
                                  <div className="flex flex-wrap gap-1">
                                    {(event.rsvp_maybe || []).map((user, index) => (
                                      <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                        {user.name || `User ${user.id}`}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* RSVP Not Attending */}
                              {event.rsvp_not_attending?.length > 0 && (
                                <div>
                                  <div className="text-xs font-medium text-red-700 mb-1">Not Attending ({event.rsvp_not_attending.length}):</div>
                                  <div className="flex flex-wrap gap-1">
                                    {(event.rsvp_not_attending || []).map((user, index) => (
                                      <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        {user.name || `User ${user.id}`}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowEventDetails(event.id)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {(isCoach || !user) && (
                        <div className="flex items-center space-x-2">
                          <label className="text-xs text-gray-600">Remind:</label>
                          <select
                            value={remindTimingByEvent[event.id] || 'now'}
                            onChange={(e) => setRemindTimingByEvent(prev => ({ ...prev, [event.id]: e.target.value }))}
                            className="px-2 py-1 text-xs border border-gray-300 rounded"
                          >
                            <option value="now">Now</option>
                            <option value="day_of">Day of</option>
                            <option value="day_before">1 day before</option>
                          </select>
                          <button
                            onClick={() => sendEventReminder(event.id)}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Send
                          </button>
                        </div>
                      )}
                      {(isCoach || !user) && (
                        <>
                          <button
                            onClick={() => handleEdit(event)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(event.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Events Message */}
      {filteredEvents.length === 0 && (
        <div className="text-center py-12">
          <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery || typeFilter !== 'all' || teamFilter !== 'all'
              ? 'Try adjusting your search criteria.'
              : 'Get started by adding your first team event.'}
          </p>
        </div>
      )}

      {/* Add/Edit Event Form */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingEvent ? 'Edit Event' : 'Add New Event'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter event title"
                />
              </div>

              {/* Team Selection - Always visible, required for practice events */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team {formData.type === 'practice' ? '*' : '(Optional)'}
                </label>
                <select
                  name="teamId"
                  value={formData.teamId || ''}
                  onChange={handleChange}
                  required={formData.type === 'practice'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                  disabled={teams.length === 0}
                >
                  <option value="">
                    {teams.length === 0 ? 'Loading teams...' : 'Select a team...'}
                  </option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id.toString()}>
                      {team.name} {team.sport ? `(${team.sport})` : ''} {team.ageGroup ? `- ${team.ageGroup}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.type === 'practice' 
                    ? 'Required for practice events. Age group will be automatically set from the selected team.'
                    : 'Optional. If selected, age group will be automatically set from the team.'}
                </p>
                {teams.length === 0 && (
                  <p className="text-xs text-yellow-600 mt-1">
                    Waiting for teams to load...
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {availableEventTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {RECURRING_EVENT_TYPES.includes(formData.type) && !editingEvent && (
                <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="repeatPracticeWeekly"
                      checked={formData.repeatPracticeWeekly}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Repeat weekly
                    </label>
                  </div>
                  {formData.repeatPracticeWeekly && (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Repeat Until *
                      </label>
                      <input
                        type="date"
                        name="recurringUntilDate"
                        value={formData.recurringUntilDate}
                        onChange={handleChange}
                        min={formData.date || undefined}
                        required={formData.repeatPracticeWeekly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Creates weekly {formData.type === 'lesson' ? 'lessons' : 'practices'} through this date.{formData.type === 'lesson' ? ' Each lesson has its own spots and fee.' : ''}
                      </p>
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          Repeat On *
                        </p>
                        <div className="grid grid-cols-4 gap-2">
                          {WEEKDAY_OPTIONS.map((day) => {
                            const isActive = formData.recurringDaysOfWeek.includes(day.value);
                            return (
                              <button
                                key={day.value}
                                type="button"
                                onClick={() => toggleRecurringDay(day.value)}
                                className={`px-2 py-1.5 text-xs rounded-md border transition-colors ${
                                  isActive
                                    ? 'bg-primary-600 text-white border-primary-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {day.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time *
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (min) *
                  </label>
                  <input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    required
                    min="15"
                    step="15"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="60"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location *
                </label>
                <LocationSearch
                  value={formData.location}
                  onChange={(value) => handleChange({ target: { name: 'location', value } })}
                  placeholder="Search for a location (e.g., Sweat Mountain Park)"
                  onSelect={(location) => {
                    handleChange({ target: { name: 'location', value: location.displayName || location.name || location.address } });
                    // Store full location data including coordinates
                    setFormData(prev => ({
                      ...prev,
                      locationData: {
                        name: location.name,
                        address: location.address,
                        coordinates: location.coordinates,
                        type: location.type,
                        placeId: location.placeId
                      }
                    }));
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Describe the event..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Attendees
                  </label>
                  <input
                    type="number"
                    name="maxAttendees"
                    value={formData.maxAttendees}
                    onChange={handleChange}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="20"
                  />
                </div>
                <div className="flex items-center pt-6">
                  <input
                    type="checkbox"
                    name="isRequired"
                    checked={formData.isRequired}
                    onChange={handleChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Required attendance
                  </label>
                </div>
              </div>

              {/* Tryout/Open Registration Options */}
              {canConfigureTryoutSettings && formData.type === 'tryout' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organization/League (Optional - leave blank for team-only tryout)
                    </label>
                    <select
                      name="organization_id"
                      value={formData.organization_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Team-only tryout</option>
                      {Array.isArray(organizations) ? organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      )) : null}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select an organization to create a league-wide tryout for all teams in that organization
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Age Group
                    </label>
                    <select
                      name="age_group_id"
                      value={formData.age_group_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">No specific age group</option>
                      {Array.isArray(ageGroups) ? ageGroups.map(ag => (
                        <option key={ag.id} value={ag.id}>{ag.name}{ag.description ? ` - ${ag.description}` : ''}</option>
                      )) : null}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select an age group for this tryout (optional)
                    </p>
                  </div>
                </div>
              )}
              
              {/* Age Group for non-tryout events */}
              {(formData.type !== 'tryout' || !canConfigureTryoutSettings) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Age Group (Optional)
                  </label>
                  <select
                    name="age_group_id"
                    value={formData.age_group_id}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">No specific age group</option>
                    {Array.isArray(ageGroups) ? ageGroups.map(ag => (
                      <option key={ag.id} value={ag.id}>{ag.name}{ag.description ? ` - ${ag.description}` : ''}</option>
                    )) : null}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select an age group for this event (optional)
                  </p>
                </div>
              )}

              {canConfigureTryoutSettings && formData.type === 'tryout' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isOpenRegistration"
                    checked={formData.isOpenRegistration}
                    onChange={handleChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Open registration (public tryout)
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Registration Fee (USD)
                  </label>
                  <input
                    type="number"
                    name="registrationFee"
                    value={formData.registrationFee}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="25"
                  />
                </div>
              </div>
              )}

              {formData.type === 'lesson' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lesson Fee (USD)
                    </label>
                    <input
                      type="number"
                      name="registrationFee"
                      value={formData.registrationFee}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="40"
                    />
                    <p className="text-xs text-gray-500 mt-1">Paid in the app when a player claims a spot.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Available Spots
                    </label>
                    <input
                      type="number"
                      name="maxSpots"
                      value={formData.maxSpots}
                      onChange={handleChange}
                      min="1"
                      step="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Unlimited"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank for no limit.</p>
                  </div>
                </div>
              )}

              {formData.type === 'fundraiser' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fundraising Goal (USD, optional)
                  </label>
                  <input
                    type="number"
                    name="fundraisingGoal"
                    value={formData.fundraisingGoal}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="5000"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    No registration needed. Share the event link and anyone can donate; leaving a name or company is optional.
                  </p>
                </div>
              )}

              {canConfigureTryoutSettings && formData.type === 'tryout' && (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-3">
                  <div>
                    <h4 className="text-sm font-semibold text-blue-900">Program</h4>
                    <p className="text-xs text-blue-700 mt-1">
                      Associate this tryout with a program. The program defines the season dates (which drive
                      player age) and the program fees used when players are assigned to teams.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Program {!editingEvent && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      name="program_id"
                      value={formData.program_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select a program</option>
                      {Array.isArray(programs) ? programs.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}{p.season ? ` (${p.season})` : ''}
                        </option>
                      )) : null}
                    </select>
                    {!effectiveProgramOrgId ? (
                      <p className="text-xs text-amber-700 mt-1">
                        Select an organization or team first to choose a program.
                      </p>
                    ) : programs.length === 0 ? (
                      <p className="text-xs text-amber-700 mt-1">
                        No active programs for this organization yet. Create one under Programs.
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        Manage programs and their fees on the Programs page.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingEvent(null);
                    setFormData({
                      title: '',
                      type: 'practice',
                      date: '',
                      time: '',
                      duration: '',
                      location: '',
        locationData: null,
                      description: '',
                      maxAttendees: '',
                      isRequired: true,
                      isOpenRegistration: false,
                      registrationFee: '',
                      maxSpots: '',
                      fundraisingGoal: '',
                      organization_id: '',
                      age_group_id: '',
                      program_id: '',
                      teamId: selectedTeam?.id ? selectedTeam.id.toString() : '',
                      repeatPracticeWeekly: false,
                      recurringUntilDate: '',
                      recurringDaysOfWeek: [],
                      programTitle: '',
                      programDescription: '',
                      programAmount: '',
                      programDueDate: '',
                      programEndDate: '',
                      programPaymentPlanEnabled: false,
                      programPaymentPlanOptions: [],
                      programAutoInvoiceOnAssignment: true,
                    });
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingEvent ? 'Update Event' : 'Add Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {showEventDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-2xl shadow-lg rounded-md bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
            {(() => {
              const event = events.find(e => e.id === showEventDetails);
              if (!event) return null;
              
              const isTryout = isTryoutEvent(event);
              // Fetch registrants when modal opens for tryout events
              if (isTryout && isCoach && !tryoutRegistrants[event.id]) {
                fetchTryoutRegistrants(event.id);
              }
              
              const registrants = tryoutRegistrants[event.id] || [];
              // For tryouts: only show registrants, for practices/games: only show RSVPs
              const allAttendees = isTryout 
                ? registrants.map(r => ({
                    id: r.player?.id || r.user?.id || r.invoiceId,
                    name: r.player?.name || r.user?.name || 'Unknown',
                    email: r.player?.email || r.user?.email || r.parent?.email || '',
                    date_of_birth: r.player?.date_of_birth || r.user?.date_of_birth || null,
                    role: r.player?.role || r.user?.role || 'player',
                    isGuest: r.isGuest,
                    rsvp: 'registered',
                    parent: r.parent
                  }))
                : [
                    ...(event.rsvp_attending || []).map(u => ({ ...u, rsvp: 'attending' })),
                    ...(event.rsvp_maybe || []).map(u => ({ ...u, rsvp: 'maybe' })),
                    ...(event.rsvp_not_attending || []).map(u => ({ ...u, rsvp: 'not_attending' }))
                  ];
              
              return (
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEventTypeColor(event.type)}`}>
                        {getEventTypeLabel(event.type)}
                      </span>
                      <h3 className="text-lg font-medium text-gray-900">{event.title}</h3>
                      {event.isRequired && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      )}
                      {/* Age Group badge in header */}
                      {event.ageGroup && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Age: {event.ageGroup.name}
                        </span>
                      )}
                      {/* Organization badge */}
                      {event.organization && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {event.organization.name}
                        </span>
                      )}
                      {/* Booked facility resource (field/cage) badge */}
                      {event.facility_resource && (
                        event.facility_resource.facility?.id ? (
                          <Link
                            to={`/facility/${event.facility_resource.facility.id}`}
                            onClick={e => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 hover:bg-teal-200"
                          >
                            <MapPinIcon className="h-3 w-3" />
                            {event.facility_resource.name}
                            {` — ${event.facility_resource.facility.name}`}
                          </Link>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                            <MapPinIcon className="h-3 w-3" />
                            {event.facility_resource.name}
                          </span>
                        )
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setShowEventDetails(null);
                        setEventDetailsTab('details');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Tabs */}
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                      <button
                        onClick={() => setEventDetailsTab('details')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${
                          eventDetailsTab === 'details'
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Details
                      </button>
                      <button
                        onClick={() => setEventDetailsTab('attendees')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${
                          eventDetailsTab === 'attendees'
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {isTryout ? `Registrants (${allAttendees.length})` : `Attendees (${allAttendees.length})`}
                      </button>
                      {isTryout && isCoach && (
                        <button
                          onClick={() => setEventDetailsTab('registrants')}
                          className={`py-4 px-1 border-b-2 font-medium text-sm ${
                            eventDetailsTab === 'registrants'
                              ? 'border-primary-500 text-primary-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Registrations ({registrants.length})
                        </button>
                      )}
                    </nav>
                  </div>
                  
                  {/* Tab Content */}
                  <div className="pt-4">
                    {eventDetailsTab === 'details' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex items-center space-x-2">
                            <CalendarIcon className="h-5 w-5 text-gray-400" />
                            <span className="text-sm text-gray-900">{formatDate(event.date)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <ClockIcon className="h-5 w-5 text-gray-400" />
                            <span className="text-sm text-gray-900">{formatTime(event.time)} ({event.duration} min)</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <MapPinIcon className="h-5 w-5 text-gray-400" />
                            {(() => {
                              const locationText = getEventLocationText(event);
                              const mapUrl = getEventMapUrl(event);
                              if (!locationText) {
                                return <span className="text-sm text-gray-900">{event.location}</span>;
                              }
                              return (
                                <a
                                  href={mapUrl || '#'}
                                  onClick={(e) => { if (!mapUrl) e.preventDefault(); }}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm text-blue-600 hover:underline"
                                >
                                  {locationText}
                                </a>
                              );
                            })()}
                          </div>
                          <div className="flex items-center space-x-2">
                            <UserGroupIcon className="h-5 w-5 text-gray-400" />
                            <span className="text-sm text-gray-900">{allAttendees.length} / {event.maxAttendees} attending</span>
                          </div>
                          {event.ageGroup && (
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                Age Group: {event.ageGroup.name}
                              </span>
                            </div>
                          )}
                          {event.organization && (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">Organization:</span>
                              <span className="text-sm text-gray-900 font-medium">{event.organization.name}</span>
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <span className="text-sm font-medium text-gray-700">Description:</span>
                          <p className="text-sm text-gray-900 mt-1">{event.description}</p>
                        </div>

                        {/* Game score */}
                        {event.type === 'game' && event.away_team_id && (
                          <div className="border-t border-gray-200 pt-4">
                            <span className="text-sm font-medium text-gray-700">Score:</span>
                            <div className="mt-2 flex items-center justify-between max-w-sm">
                              <span className="text-sm text-gray-900">
                                {event.team?.name || 'Home'}
                                {event.winner_team_id === event.team_id && <span className="ml-1 text-xs text-green-600">(W)</span>}
                              </span>
                              {event.home_score !== null && event.home_score !== undefined ? (
                                <span className="text-sm font-semibold text-gray-900">{event.home_score}</span>
                              ) : isCoach || ['admin', 'org_admin', 'app_manager'].includes(user?.role) ? (
                                <input type="number" min="0" placeholder="H"
                                  value={gameScoreDraft.home}
                                  onChange={(e) => setGameScoreDraft(d => ({ ...d, home: e.target.value }))}
                                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded" />
                              ) : (
                                <span className="text-xs text-gray-400">Not yet played</span>
                              )}
                            </div>
                            <div className="mt-1 flex items-center justify-between max-w-sm">
                              <span className="text-sm text-gray-900">
                                {event.away_team?.name || 'Away'}
                                {event.winner_team_id === event.away_team_id && <span className="ml-1 text-xs text-green-600">(W)</span>}
                              </span>
                              {event.away_score !== null && event.away_score !== undefined ? (
                                <span className="text-sm font-semibold text-gray-900">{event.away_score}</span>
                              ) : (isCoach || ['admin', 'org_admin', 'app_manager'].includes(user?.role)) ? (
                                <input type="number" min="0" placeholder="A"
                                  value={gameScoreDraft.away}
                                  onChange={(e) => setGameScoreDraft(d => ({ ...d, away: e.target.value }))}
                                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded" />
                              ) : null}
                            </div>
                            {(event.home_score === null || event.home_score === undefined) &&
                              (isCoach || ['admin', 'org_admin', 'app_manager'].includes(user?.role)) && (
                              <button
                                onClick={() => handleSaveGameScore(event.id, gameScoreDraft.home, gameScoreDraft.away)}
                                disabled={gameScoreDraft.home === '' || gameScoreDraft.away === ''}
                                className="mt-2 px-3 py-1.5 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-40"
                              >
                                Save Score
                              </button>
                            )}
                          </div>
                        )}

                        {/* Registration/RSVP Section */}
                        {event.type === 'lesson' ? (
                          <div className="border-t border-gray-200 pt-4">
                            <LessonSignupPanel
                              event={event}
                              onClaimSpot={() => handleRegisterForEvent(event.id)}
                              onWalkUp={canAccessWalkUpRegistration() ? () => openWalkUpRegistration(event.id) : null}
                            />
                          </div>
                        ) : event.type === 'fundraiser' ? (
                          <div className="border-t border-gray-200 pt-4">
                            <FundraiserPanel
                              event={event}
                              onDonate={() => handleRegisterForEvent(event.id)}
                              canViewDonors={canShareEventLink()}
                              authToken={user?.token}
                            />
                          </div>
                        ) : isTryout ? (
                          <div className="border-t border-gray-200 pt-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-700">Registration:</span>
                              <div className="flex items-center space-x-4 text-sm text-gray-600">
                                <span className="flex items-center space-x-1">
                                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                                  <span>{registrants.length} Registered</span>
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleRegisterForEvent(event.id)}
                                className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                              >
                                Register for Tryout
                              </button>
                              {event.registrationFee > 0 && (
                                <span className="text-sm text-gray-600">
                                  Registration Fee: ${Number(event.registrationFee).toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="border-t border-gray-200 pt-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-700">RSVP Status:</span>
                              <div className="flex items-center space-x-4 text-sm text-gray-600">
                                <span className="flex items-center space-x-1">
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                  <span>{event.rsvp_attending?.length || 0} Attending</span>
                                </span>
                                <span className="flex items-center space-x-1">
                                  <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                                  <span>{event.rsvp_maybe?.length || 0} Maybe</span>
                                </span>
                                <span className="flex items-center space-x-1">
                                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                  <span>{event.rsvp_not_attending?.length || 0} Not Attending</span>
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">Your RSVP:</span>
                              <button
                                onClick={() => handleRsvp(event.id, 'attending')}
                                className={`px-3 py-1 text-sm font-medium rounded-full transition-colors ${
                                  getUserRsvpStatus(event.id) === 'attending'
                                    ? 'bg-green-100 text-green-800 border border-green-200'
                                    : 'bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-green-700'
                                }`}
                              >
                                ✓ Attending
                              </button>
                              <button
                                onClick={() => handleRsvp(event.id, 'maybe')}
                                className={`px-3 py-1 text-sm font-medium rounded-full transition-colors ${
                                  getUserRsvpStatus(event.id) === 'maybe'
                                    ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                    : 'bg-gray-100 text-gray-700 hover:bg-yellow-50 hover:text-yellow-700'
                                }`}
                              >
                                ? Maybe
                              </button>
                              <button
                                onClick={() => handleRsvp(event.id, 'not_attending')}
                                className={`px-3 py-1 text-sm font-medium rounded-full transition-colors ${
                                  getUserRsvpStatus(event.id) === 'not_attending'
                                    ? 'bg-red-100 text-red-800 border border-red-200'
                                    : 'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-700'
                                }`}
                              >
                                ✗ Not Attending
                              </button>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex justify-end pt-4">
                          {canShareEventLink() && (
                            <>
                              <button
                                onClick={() => copyShareLink(event.id)}
                                className="mr-2 inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                              >
                                <ShareIcon className="h-4 w-4 mr-2" /> Share Link
                              </button>
                              {isTryout && canAccessWalkUpRegistration() && (
                                <button
                                  onClick={() => openWalkUpRegistration(event.id)}
                                  className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700"
                                >
                                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                  Walk-Up Registration
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {eventDetailsTab === 'attendees' && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-md font-medium text-gray-900">
                            {isTryout ? `Registrants (${allAttendees.length})` : `Attendees (${allAttendees.length})`}
                          </h4>
                          {isTryout && registrants.length > 0 && isCoach && (
                            <a
                              href={`/tryout-assignment?eventId=${event.id}`}
                              className="text-sm text-primary-600 hover:text-primary-800 underline"
                            >
                              Assign to Teams →
                            </a>
                          )}
                        </div>
                        
                        {allAttendees.length > 0 ? (
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {isTryout ? (
                              /* Tryout Events - Show Registrants Only */
                              allAttendees.map((attendee, idx) => (
                                <div key={attendee.id || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-medium text-gray-900">
                                        {attendee.name || 'Unknown'}
                                      </span>
                                      {attendee.isGuest && (
                                        <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Guest</span>
                                      )}
                                      <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Registered</span>
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                      {attendee.email}
                                      {attendee.parent && attendee.parent.name !== attendee.name && (
                                        <span className="ml-2">(Parent: {attendee.parent.name})</span>
                                      )}
                                    </div>
                                    {attendee.date_of_birth && (
                                      <div className="text-xs text-gray-400 mt-0.5">
                                        DOB: {(parseLocalDate(attendee.date_of_birth) || new Date(attendee.date_of_birth)).toLocaleDateString()}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {attendee.role || 'Player'}
                                  </div>
                                </div>
                              ))
                            ) : (
                              /* Practice/Game Events - Show RSVP Only */
                              <>
                                {/* Attending */}
                                {allAttendees.filter(a => a.rsvp === 'attending').length > 0 && (
                                  <div className="mb-4">
                                    <h5 className="text-sm font-semibold text-green-700 mb-2">
                                      Attending ({allAttendees.filter(a => a.rsvp === 'attending').length})
                                    </h5>
                                    <div className="space-y-2">
                                      {allAttendees
                                        .filter(a => a.rsvp === 'attending')
                                        .map((attendee, idx) => (
                                          <div key={attendee.id || idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                            <div className="flex-1">
                                              <div className="flex items-center space-x-2">
                                                <span className="text-sm font-medium text-gray-900">
                                                  {attendee.name || 'Unknown'}
                                                </span>
                                                <span className="text-xs text-green-600 bg-green-100 px-1.5 py-0.5 rounded">Attending</span>
                                              </div>
                                              <div className="text-xs text-gray-600 mt-1">
                                                {attendee.email}
                                              </div>
                                            </div>
                                            <div className="text-xs text-gray-500">
                                              {attendee.role || 'Player'}
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Maybe */}
                                {allAttendees.filter(a => a.rsvp === 'maybe').length > 0 && (
                                  <div className="mb-4">
                                    <h5 className="text-sm font-semibold text-yellow-700 mb-2">
                                      Maybe ({allAttendees.filter(a => a.rsvp === 'maybe').length})
                                    </h5>
                                    <div className="space-y-2">
                                      {allAttendees
                                        .filter(a => a.rsvp === 'maybe')
                                        .map((attendee, idx) => (
                                          <div key={attendee.id || idx} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                                            <div className="flex-1">
                                              <div className="flex items-center space-x-2">
                                                <span className="text-sm font-medium text-gray-900">
                                                  {attendee.name || 'Unknown'}
                                                </span>
                                                <span className="text-xs text-yellow-600 bg-yellow-100 px-1.5 py-0.5 rounded">Maybe</span>
                                              </div>
                                              <div className="text-xs text-gray-600 mt-1">
                                                {attendee.email}
                                              </div>
                                            </div>
                                            <div className="text-xs text-gray-500">
                                              {attendee.role || 'Player'}
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Not Attending */}
                                {allAttendees.filter(a => a.rsvp === 'not_attending').length > 0 && (
                                  <div>
                                    <h5 className="text-sm font-semibold text-red-700 mb-2">
                                      Not Attending ({allAttendees.filter(a => a.rsvp === 'not_attending').length})
                                    </h5>
                                    <div className="space-y-2">
                                      {allAttendees
                                        .filter(a => a.rsvp === 'not_attending')
                                        .map((attendee, idx) => (
                                          <div key={attendee.id || idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                                            <div className="flex-1">
                                              <div className="flex items-center space-x-2">
                                                <span className="text-sm font-medium text-gray-900">
                                                  {attendee.name || 'Unknown'}
                                                </span>
                                                <span className="text-xs text-red-600 bg-red-100 px-1.5 py-0.5 rounded">Not Attending</span>
                                              </div>
                                              <div className="text-xs text-gray-600 mt-1">
                                                {attendee.email}
                                              </div>
                                            </div>
                                            <div className="text-xs text-gray-500">
                                              {attendee.role || 'Player'}
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            {isTryout ? 'No registrants yet' : 'No attendees yet'}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {eventDetailsTab === 'registrants' && isTryout && isCoach && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-md font-medium text-gray-900">Tryout Registrations</h4>
                          <a
                            href={`/tryout-assignment?eventId=${event.id}`}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          >
                            Assign to Teams →
                          </a>
                        </div>
                        
                        {registrants.length > 0 ? (
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {registrants.map((reg, idx) => {
                              const displayName = reg.player?.name || reg.user?.name || 'Unknown';
                              const displayEmail = reg.player?.email || reg.user?.email || reg.parent?.email || '';
                              const isGuest = reg.isGuest;
                              
                              return (
                                <div key={reg.invoiceId || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-medium text-gray-900">
                                        {displayName}
                                      </span>
                                      {isGuest && (
                                        <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Guest</span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                      {displayEmail}
                                      {reg.parent && reg.parent.name !== displayName && (
                                        <span className="ml-2">(Parent: {reg.parent.name})</span>
                                      )}
                                    </div>
                                    {(reg.player?.date_of_birth || reg.user?.date_of_birth) && (
                                      <div className="text-xs text-gray-400 mt-0.5">
                                        DOB: {(() => {
                                          const dob = reg.player?.date_of_birth || reg.user.date_of_birth;
                                          return (parseLocalDate(dob) || new Date(dob)).toLocaleDateString();
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-600 ml-4">
                                    ${Number(reg.amount || 0).toFixed(2)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            No registrations yet
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Walk-Up Registration Modal */}
      {showWalkUpRegistration && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-5 border w-2xl shadow-lg rounded-md bg-white max-w-2xl mb-20 sm:mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Walk-Up Registration</h3>
              <button
                onClick={() => {
                  resetWalkUpModalState();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* A plain div, not a <form> — StripePaymentForm below renders its own
                <form>, and nested forms are invalid HTML whose native submit-event
                bubbling can silently re-trigger this outer handler mid-payment. */}
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  Register a player on-site for this tryout. All fields are required except phone number.
                </p>
              </div>

              <div>
                <label htmlFor="playerName" className="block text-sm font-medium text-gray-700 mb-1">
                  Player Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="playerName"
                  required
                  disabled={!!walkUpStripeConfig}
                  value={walkUpFormData.playerName}
                  onChange={(e) => setWalkUpFormData(prev => ({ ...prev, playerName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter player's full name"
                />
              </div>

              <div>
                <label htmlFor="playerDob" className="block text-sm font-medium text-gray-700 mb-1">
                  Player Date of Birth <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="playerDob"
                  required
                  disabled={!!walkUpStripeConfig}
                  value={walkUpFormData.playerDob}
                  onChange={(e) => setWalkUpFormData(prev => ({ ...prev, playerDob: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  max={new Date().toISOString().split('T')[0]}
                />
                <p className="mt-1 text-xs text-gray-500">Format: MM/DD/YYYY</p>
              </div>

              <div>
                <label htmlFor="parentName" className="block text-sm font-medium text-gray-700 mb-1">
                  Parent/Guardian Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="parentName"
                  required
                  disabled={!!walkUpStripeConfig}
                  value={walkUpFormData.parentName}
                  onChange={(e) => setWalkUpFormData(prev => ({ ...prev, parentName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter parent/guardian's full name"
                />
              </div>

              <div>
                <label htmlFor="parentEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  Parent/Guardian Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="parentEmail"
                  required
                  disabled={!!walkUpStripeConfig}
                  value={walkUpFormData.parentEmail}
                  onChange={(e) => setWalkUpFormData(prev => ({ ...prev, parentEmail: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter parent/guardian's email"
                />
              </div>

              <div>
                <label htmlFor="parentPhone" className="block text-sm font-medium text-gray-700 mb-1">
                  Parent/Guardian Phone (Optional)
                </label>
                <input
                  type="tel"
                  id="parentPhone"
                  disabled={!!walkUpStripeConfig}
                  value={walkUpFormData.parentPhone}
                  onChange={(e) => setWalkUpFormData(prev => ({ ...prev, parentPhone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="(555) 123-4567"
                />
              </div>

              {/* Payment Section - Show if Stripe config is ready */}
              {walkUpStripeConfig && walkUpPendingRegistration && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-yellow-800 font-medium">
                      Payment Required: ${(walkUpStripeConfig.amount / 100).toFixed(2)}
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Complete payment below to finish registration.
                    </p>
                  </div>
                  <StripePaymentForm
                    config={walkUpStripeConfig}
                    onComplete={handleWalkUpPaymentComplete}
                  />
                </div>
              )}

              {/* Form Actions - Hide if payment form is showing */}
              {!walkUpStripeConfig && (
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      resetWalkUpModalState();
                    }}
                    className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    disabled={walkUpSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleWalkUpRegistration}
                    className="px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={walkUpSubmitting}
                  >
                    {walkUpSubmitting ? 'Processing...' : (walkUpEventData?.registrationFee > 0 ? 'Continue to Payment' : 'Register Player')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        isDangerous={confirmState?.isDangerous}
        onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
};

export default Events;
